import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BookOpen,
  Brain,
  Copy,
  Download,
  FileCode,
  ListOrdered,
  Monitor,
  RefreshCw,
  Shield,
  Sparkles,
  Target,
  Terminal,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { NetworkScanLive, NetworkScanTerminalBanner } from '../components/NetworkScanLive';
import { api } from '../lib/api';
import { launcherInstallHint, wakeLocalConnector } from '../lib/localConnectorLauncher';
import { usePilotContext } from '../hooks/usePilotContext';
import { useNetworkScanJobOptional } from '../contexts/NetworkScanJobContext';
import { AertaxRedAiConsole } from '../components/aertago/AertaxRedAiConsole';
import AertagoConnectorSetupPanel from '../components/aertago/AertagoConnectorSetupPanel';

const REQ_OVERVIEW_MS = 45000;
const REQ_STATUS_MS = 35000;
const REQ_SNAPSHOT_MS = 120000;
const CONNECTOR_STALE_SEC = 120;
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** Ordered narrative for the AERTAGO controlled-lab experience (isolated environment only). */
const AERTAGO_FLOW_STEPS = [
  {
    title: 'Scope & contract',
    detail:
      'Isolated lab only; authorized targets; real-time runs on allowlisted assets; kill switch and full audit trail.',
  },
  {
    title: 'Discovery',
    detail: 'Asset inventory and exposure map with confidence—what is known vs inferred on each host.',
  },
  {
    title: 'Vulnerability & risk',
    detail: 'Ranked weaknesses: CVEs/KEV where versions are known, plus misconfigurations (e.g. SMB, RDP, policy).',
  },
  {
    title: 'Red AI — autonomous attack paths & live access',
    detail:
      'Evidence-anchored chains (MITRE) with Red AI orchestration over run artifacts and range policy; offensive tooling runs where you wire MCP/executor. Live desktop is brokered through the viewer gateway and session flow — configure the relay here, not on lab workstations. Set AERTAGO_SUPERVISED_MODE=true later if you want approval-first messaging and gates.',
  },
  {
    title: 'Blue AI — defense analysis',
    detail: 'What defenders would see; what could be missed; cost to close visibility gaps—same facts as the attack narrative.',
  },
  {
    title: 'Plain-language explanations',
    detail: 'Findings translated for any audience; technical IDs available as footnotes for verification.',
  },
  {
    title: 'Prioritized remediation',
    detail: 'Ordered fixes with effort, blast radius, automation vs maintenance window, and how to verify each.',
  },
  {
    title: 'Retest & evidence',
    detail: 'Apply a fix; re-run the same checks; show score and finding deltas with immutable job-linked reports.',
  },
  {
    title: 'Conversational Q&A',
    detail: 'Ask Red/Blue why—answers grounded in the run (scan IDs, findings, logs), not generic advice.',
  },
];

const DEFAULT_EXPERIMENT_CONTRACT_TEXT = `{
  "schema_version": 1,
  "experiment_id": "lab.cyber-range.session",
  "description": "Formal gates for this lab session",
  "timebox_minutes": 120,
  "require_active_exercise_run": true,
  "max_offensive_steps": 24,
  "replay_mode": "off",
  "replay_snapshot_ids": [],
  "gates": [],
  "evaluation_metrics": []
}`;

const DEFAULT_EXPERIMENT_EVAL_METRICS_TEXT = `{
  "score": 0,
  "reduced_findings": 0
}`;

function aertagoReadinessBadgeVariant(readiness) {
  const r = String(readiness || '').toLowerCase();
  if (r === 'ready') return 'success';
  if (r === 'partial') return 'warning';
  if (r === 'degraded') return 'destructive';
  return 'outline';
}

function gatewayHealthBadgeVariant(health) {
  if (!health) return 'outline';
  if (health.path_or_route_mismatch) return 'destructive';
  if (health.reachable && health.embed_likely_blocked === false) return 'success';
  if (health.reachable && health.embed_likely_blocked) return 'warning';
  return 'destructive';
}

function connectorStatusBadgeVariant(status) {
  if (status === 'online') return 'success';
  if (status === 'stale') return 'warning';
  return 'outline';
}

function connectorStatusUiLabel(status, connectorRequired = false) {
  if (status === 'online') return 'online';
  if (status === 'stale') return 'stale';
  if (connectorRequired) return 'required (offline)';
  return 'offline';
}

function connectorStatusLabel(connectorState) {
  const seen = connectorState?.last_seen_at_utc ? Date.parse(connectorState.last_seen_at_utc) : NaN;
  if (!connectorState?.active_connector_id || Number.isNaN(seen)) return 'offline';
  const ageSec = Math.max(0, Math.round((Date.now() - seen) / 1000));
  if (ageSec > CONNECTOR_STALE_SEC) return 'stale';
  return 'online';
}

function accessPathBadgeVariant(path) {
  const p = String(path || '').toLowerCase();
  if (p === 'oob') return 'success';
  if (p === 'aertago_gateway') return 'warning';
  if (p === 'blocked_no_approved_access_path') return 'destructive';
  return 'outline';
}

function winnerBadgeClass(w) {
  if (!w) return 'bg-muted text-muted-foreground';
  const u = String(w).toUpperCase();
  if (u.includes('RED')) return 'bg-rose-500/15 text-rose-300 border-rose-500/30';
  if (u.includes('BLUE')) return 'bg-sky-500/15 text-sky-300 border-sky-500/30';
  if (u.includes('DRAW')) return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  return 'bg-secondary text-secondary-foreground';
}

function formatAgeSeconds(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  if (s < 90) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 120) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} d ago`;
}

function orchestratorStateBadgeVariant(state) {
  if (state === 'running') return 'success';
  if (state === 'missing') return 'destructive';
  if (state === 'active') return 'warning';
  if (state === 'standby') return 'warning';
  return 'secondary';
}

function orchestratorViaLabel(via) {
  if (via === 'docker_engine_socket') return 'Docker (engine)';
  if (via === 'docker_cli') return 'Docker (CLI)';
  if (via === 'orchestrator_log_file') return 'Host log file';
  return via || '—';
}

function lastNonEmptyLogLine(text) {
  if (!text || typeof text !== 'string') return '';
  const lines = text.trim().split('\n');
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const t = lines[i].trim();
    if (t) return t.length > 160 ? `${t.slice(0, 157)}…` : t;
  }
  return '';
}

/** Align with api.dojo_lab._winner_side_label */
function normalizeWinnerSide(w) {
  const u = String(w ?? '')
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (!u.trim()) return 'unknown';
  if (u.includes('DRAW') || u.includes('TIE')) return 'draw';
  if (u.includes('BLUE')) return 'blue';
  if (u.includes('RED')) return 'red';
  return 'unknown';
}

function buildMatchAnalytics(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const chrono = list.slice().reverse();
  let redWins = 0;
  let blueWins = 0;
  let draws = 0;
  let unknown = 0;
  chrono.forEach((row) => {
    const side = row?.winner_side || normalizeWinnerSide(row?.winner);
    if (side === 'red') redWins += 1;
    else if (side === 'blue') blueWins += 1;
    else if (side === 'draw') draws += 1;
    else unknown += 1;
  });
  const trendData = [];
  let rw = 0;
  let bw = 0;
  let dr = 0;
  chrono.forEach((row, idx) => {
    const side = row?.winner_side || normalizeWinnerSide(row?.winner);
    if (side === 'red') rw += 1;
    else if (side === 'blue') bw += 1;
    else if (side === 'draw') dr += 1;
    const n = idx + 1;
    trendData.push({
      match: n,
      redWinRate: Math.round((rw / n) * 1000) / 10,
      blueWinRate: Math.round((bw / n) * 1000) / 10,
      drawRate: Math.round((dr / n) * 1000) / 10,
    });
  });
  const allWinRatesFlat =
    trendData.length > 0 && trendData.every((d) => d.redWinRate === 0 && d.blueWinRate === 0);
  return {
    chrono,
    trendData,
    outcomeCounts: { red: redWins, blue: blueWins, draw: draws, unknown },
    allWinRatesFlat,
  };
}

function rangeEngineFriendlyTitle(state) {
  if (state === 'running') return 'Range engine is live';
  if (state === 'active') return 'Lab data is fresh';
  if (state === 'standby') return 'Standing by on recent lab data';
  if (state === 'not_running') return 'Range engine is idle';
  if (state === 'missing') return 'Orchestrator script not found';
  return 'Range status';
}

function rangeEngineFriendlySubtitle(rs, art) {
  if (rs?.state === 'running') {
    return 'Matches and learning artifacts are being produced on the server.';
  }
  if (art?.age_seconds != null && art.age_seconds < 3600) {
    return `Last lab write ${formatAgeSeconds(art.age_seconds)}.`;
  }
  return 'This view updates when you refresh or when auto-refresh is on.';
}

/**
 * AERTAGO (Cyber Range) — controlled lab experience; visual exercise status + optional full JSON export.
 */
export function CyberRangePage() {
  const { context } = usePilotContext();
  const networkScanJob = useNetworkScanJobOptional();
  const lastPartialRefreshWarnRef = useRef(0);
  /** After pilot `/api/network-scan/.../jobs` completes, merge hosts into AERTAGO allowlist once per job. */
  const lastNetScanAllowlistSyncJobRef = useRef('');
  const viewerSectionRef = useRef(null);
  const [overview, setOverview] = useState(null);
  const [status, setStatus] = useState(null);
  const [learning, setLearning] = useState(null);
  const [policyQueue, setPolicyQueue] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loadingMain, setLoadingMain] = useState(true);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [error, setError] = useState(null);
  const [logPayload, setLogPayload] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [recBusyId, setRecBusyId] = useState(null);
  /** Live tail of orchestrator stdout (same endpoint as Docker / host file on Render). */
  const [liveOrchestratorTail, setLiveOrchestratorTail] = useState(true);
  const [controlConfig, setControlConfig] = useState(null);
  const [controlBusy, setControlBusy] = useState(false);
  const [controlTargetInput, setControlTargetInput] = useState('');
  const [controlTarget, setControlTarget] = useState('');
  const [controlProtocol, setControlProtocol] = useState('rdp');
  const [controlReason, setControlReason] = useState('Instructor-approved AERTAGO viewer session.');
  const [approvalId, setApprovalId] = useState('');
  const [activeSessionId, setActiveSessionId] = useState('');
  // UI-only Red AI workflow scaffold (real execution is wired by operator later).
  const [redAiUiStatus, setRedAiUiStatus] = useState('not_started'); // not_started | awaiting_backend | reported_success | reported_failed
  const [redAiUiEvidenceText, setRedAiUiEvidenceText] = useState('{}');
  const redAiAutoOpenGuardRef = useRef('');
  const [viewerFocusArmed, setViewerFocusArmed] = useState(false);
  const [redAiScanTargets, setRedAiScanTargets] = useState('');
  const [redAiScanPorts, setRedAiScanPorts] = useState('');
  const [redAiScanIncludeNoPorts, setRedAiScanIncludeNoPorts] = useState(true);
  const [redAiScanBusy, setRedAiScanBusy] = useState(false);
  const [gatewayHealth, setGatewayHealth] = useState(null);
  const [gatewayBusy, setGatewayBusy] = useState(false);
  const [coldStartBusy, setColdStartBusy] = useState(false);
  const [planAssistBusy, setPlanAssistBusy] = useState(false);
  const [planAssistFocus, setPlanAssistFocus] = useState('');
  const [planAssistText, setPlanAssistText] = useState('');
  const [gatewayRdpUrl, setGatewayRdpUrl] = useState('');
  const [gatewayVncUrl, setGatewayVncUrl] = useState('');
  const [gatewayRdpProbeUrl, setGatewayRdpProbeUrl] = useState('');
  const [gatewayVncProbeUrl, setGatewayVncProbeUrl] = useState('');
  const [autoScanBusy, setAutoScanBusy] = useState(false);
  const [labBootstrapBusy, setLabBootstrapBusy] = useState(false);
  const [labBootstrapSteps, setLabBootstrapSteps] = useState(null);
  const [autoScanSummary, setAutoScanSummary] = useState('');
  const [autoScanDiagnostics, setAutoScanDiagnostics] = useState(null);
  /** Same live scan animation as Network Scan page while connector job is queued/running; banner after terminal state. */
  const [autoScanJobVisual, setAutoScanJobVisual] = useState(null);
  /** Reuse the same animation style for exercise run start. */
  const [runStartVisual, setRunStartVisual] = useState(null);
  const [experimentName, setExperimentName] = useState('');
  const [timeboxMinutes, setTimeboxMinutes] = useState('');
  const [runEndNote, setRunEndNote] = useState('');
  const [blueSnap, setBlueSnap] = useState(null);
  const [blueSnapLoading, setBlueSnapLoading] = useState(false);
  const [blueSnapNowMs, setBlueSnapNowMs] = useState(Date.now());
  const [blueSnapEventLine, setBlueSnapEventLine] = useState('latest: waiting for blue snapshot...');
  const blueSnapPrevSigRef = useRef(null);
  const [experimentContractText, setExperimentContractText] = useState(DEFAULT_EXPERIMENT_CONTRACT_TEXT);
  const [experimentGateBusy, setExperimentGateBusy] = useState(false);
  const [experimentReadiness, setExperimentReadiness] = useState(null);
  const [experimentValidateResult, setExperimentValidateResult] = useState(null);
  const [experimentHarnessResult, setExperimentHarnessResult] = useState(null);
  const [experimentCompareRunA, setExperimentCompareRunA] = useState('');
  const [experimentCompareRunB, setExperimentCompareRunB] = useState('');
  const [experimentCompareFileA, setExperimentCompareFileA] = useState('');
  const [experimentCompareFileB, setExperimentCompareFileB] = useState('');
  const [experimentCompareResult, setExperimentCompareResult] = useState(null);
  const [experimentEvalRunId, setExperimentEvalRunId] = useState('');
  const [experimentEvalLabel, setExperimentEvalLabel] = useState('');
  const [experimentEvalMetricsText, setExperimentEvalMetricsText] = useState(DEFAULT_EXPERIMENT_EVAL_METRICS_TEXT);
  const [experimentEvalLastWritten, setExperimentEvalLastWritten] = useState(null);
  const [researchRsGoal, setResearchRsGoal] = useState('Brokered remote desktop (Red AI lab)');
  const [researchRsTargets, setResearchRsTargets] = useState('');
  const [researchRsExperimentName, setResearchRsExperimentName] = useState('Red AI research session');
  const [researchRsControlPlane, setResearchRsControlPlane] = useState('aertago');
  const [researchRsSessionId, setResearchRsSessionId] = useState('');
  const [researchRsSession, setResearchRsSession] = useState(null);
  const [researchRsBusy, setResearchRsBusy] = useState(false);
  const [researchRsStepNote, setResearchRsStepNote] = useState('');
  const [researchRsPlannerStatus, setResearchRsPlannerStatus] = useState(null);
  const [researchTargetRows, setResearchTargetRows] = useState([]);
  const [researchTargetBusy, setResearchTargetBusy] = useState(false);
  const [researchStrategyTitle, setResearchStrategyTitle] = useState('');
  const [researchStrategyMethod, setResearchStrategyMethod] = useState('approved_safe_path');
  const [researchStrategyTarget, setResearchStrategyTarget] = useState('');
  const [researchStrategyRationale, setResearchStrategyRationale] = useState('');
  const [researchStrategyEvidenceText, setResearchStrategyEvidenceText] = useState('{}');
  const [researchStrategyOutcome, setResearchStrategyOutcome] = useState('failed');
  const [researchStrategyNote, setResearchStrategyNote] = useState('');
  const runStartVisualTimerRef = useRef(null);
  const embeddedAertaxCaps = overview?.aertago_capabilities?.embedded_aertax_agent;

  const loadOrchestratorLogs = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLogLoading(true);
    try {
      const r = await api.get('/api/dojo-lab/orchestrator-logs?tail=160', { timeoutMs: 20000 });
      setLogPayload(r);
      if (!silent) {
        if (r.ok) toast.success('Orchestrator log updated');
        else toast.error(r.hint || r.reason || r.stderr || 'Could not load orchestrator logs');
      }
    } catch (e) {
      if (!silent) {
        setLogPayload(null);
        toast.error(e.message || 'Failed to load orchestrator logs');
      } else {
        setLogPayload((prev) => prev ?? { ok: false, hint: e.message || 'Log request failed' });
      }
    } finally {
      if (!silent) setLogLoading(false);
    }
  }, []);

  const loadAertagoControlConfig = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      const cfg = await api.get('/api/dojo-lab/aertago-control/config', { timeoutMs: 20000 });
      setControlConfig(cfg);
      setGatewayRdpUrl(String(cfg?.gateway_urls?.rdp || ''));
      setGatewayVncUrl(String(cfg?.gateway_urls?.vnc || ''));
      setGatewayRdpProbeUrl(String(cfg?.gateway_probe_urls?.rdp || ''));
      setGatewayVncProbeUrl(String(cfg?.gateway_probe_urls?.vnc || ''));
      const targets = Array.isArray(cfg?.allowlist_targets) ? cfg.allowlist_targets : [];
      setControlTargetInput(targets.join('\n'));
      setControlTarget((prev) => (prev ? prev : targets[0] || ''));
      const sessions = Array.isArray(cfg?.sessions_active) ? cfg.sessions_active : [];
      setActiveSessionId((prev) => {
        if (prev && sessions.some((s) => s?.id === prev)) return prev;
        return sessions[0]?.id || '';
      });
      if (!silent) toast.success('AERTAGO control config updated');
    } catch (e) {
      if (!silent) toast.error(e.message || 'Failed to load AERTAGO control config');
      setControlConfig((prev) => prev ?? null);
    }
  }, []);

  const probeGatewayHealth = useCallback(
    async (protocol = controlProtocol, opts = {}) => {
      const silent = opts.silent === true;
      if (!silent) setGatewayBusy(true);
      try {
        const res = await api.get(
          `/api/dojo-lab/aertago-control/gateway-health?protocol=${encodeURIComponent(protocol || 'rdp')}`,
          { timeoutMs: 15000 }
        );
        setGatewayHealth(res);
        if (!silent) toast.success('Gateway health updated');
      } catch (e) {
        if (!silent) toast.error(e.message || 'Gateway probe failed');
        setGatewayHealth((prev) => prev ?? { reachable: false, reason: e.message || 'request_failed' });
      } finally {
        if (!silent) setGatewayBusy(false);
      }
    },
    [controlProtocol]
  );

  const refreshColdStartBootstrap = useCallback(async () => {
    setColdStartBusy(true);
    try {
      await api.post('/api/dojo-lab/aertago-control/cold-start/refresh', {}, { timeoutMs: 25000 });
      toast.success('Gateway autodetect refreshed (control plane)');
      await loadAertagoControlConfig({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Cold-start refresh failed');
    } finally {
      setColdStartBusy(false);
    }
  }, [loadAertagoControlConfig]);

  const runPlanAssist = useCallback(async () => {
    setPlanAssistBusy(true);
    try {
      const r = await api.post(
        '/api/dojo-lab/aertago-control/plan-assist',
        { focus: planAssistFocus.trim() || undefined },
        { timeoutMs: 120000 }
      );
      setPlanAssistText(String(r?.narrative || ''));
      toast.success(r?.source === 'llm' ? 'Red AI planner (LLM)' : 'Red AI planner (deterministic fallback)');
    } catch (e) {
      toast.error(e.message || 'Plan assist failed');
    } finally {
      setPlanAssistBusy(false);
    }
  }, [planAssistFocus]);

  const saveRuntimeGatewayUrls = useCallback(async () => {
    setGatewayBusy(true);
    try {
      const body = {
        rdp_url: gatewayRdpUrl.trim(),
        vnc_url: gatewayVncUrl.trim(),
        rdp_probe_url: gatewayRdpProbeUrl.trim(),
        vnc_probe_url: gatewayVncProbeUrl.trim(),
      };
      const r = await api.post('/api/dojo-lab/aertago-control/gateway-urls', body, { timeoutMs: 20000 });
      setControlConfig((prev) => ({
        ...(prev || {}),
        gateway_urls: r?.gateway_urls || {},
        gateway_probe_urls: r?.gateway_probe_urls || {},
        gateway_urls_source: r?.gateway_urls_source || 'runtime_override',
      }));
      toast.success('Gateway URLs updated');
      await probeGatewayHealth(controlProtocol, { silent: true });
    } catch (e) {
      toast.error(e.message || 'Could not update gateway URLs');
    } finally {
      setGatewayBusy(false);
    }
  }, [controlProtocol, gatewayRdpProbeUrl, gatewayRdpUrl, gatewayVncProbeUrl, gatewayVncUrl, probeGatewayHealth]);

  const resetRuntimeGatewayUrlsToEnv = useCallback(async () => {
    setGatewayBusy(true);
    try {
      const r = await api.post('/api/dojo-lab/aertago-control/gateway-urls', { clear_all: true }, { timeoutMs: 20000 });
      setGatewayRdpUrl(String(r?.gateway_urls?.rdp || ''));
      setGatewayVncUrl(String(r?.gateway_urls?.vnc || ''));
      setGatewayRdpProbeUrl(String(r?.gateway_probe_urls?.rdp || ''));
      setGatewayVncProbeUrl(String(r?.gateway_probe_urls?.vnc || ''));
      setControlConfig((prev) => ({
        ...(prev || {}),
        gateway_urls: r?.gateway_urls || {},
        gateway_probe_urls: r?.gateway_probe_urls || {},
        gateway_urls_source: r?.gateway_urls_source || 'env',
      }));
      toast.success('Runtime gateway override cleared (env values active)');
      await probeGatewayHealth(controlProtocol, { silent: true });
    } catch (e) {
      toast.error(e.message || 'Could not clear runtime gateway override');
    } finally {
      setGatewayBusy(false);
    }
  }, [controlProtocol, probeGatewayHealth]);

  const loadBlueSnapshot = useCallback(
    async (opts = {}) => {
      const silent = opts.silent === true;
      if (!context?.customer_id) {
        setBlueSnap(null);
        return;
      }
      if (!silent) setBlueSnapLoading(true);
      try {
        const res = await api.get(
          `/api/dojo-lab/aertago-control/blue-snapshot?customer_id=${encodeURIComponent(context.customer_id)}`,
          { timeoutMs: 45000 }
        );
        setBlueSnap(res);
      } catch (e) {
        if (!silent) toast.error(e.message || 'Blue snapshot failed');
        setBlueSnap((prev) => prev ?? null);
      } finally {
        if (!silent) setBlueSnapLoading(false);
      }
    },
    [context?.customer_id]
  );

  const refreshMain = useCallback(async () => {
    setLoadingMain(true);
    setError(null);
    try {
      const [oRes, stRes] = await Promise.allSettled([
        api.get('/api/dojo-lab/overview', { timeoutMs: REQ_OVERVIEW_MS }),
        api.get('/api/dojo-lab/status', { timeoutMs: REQ_STATUS_MS }),
      ]);
      const overviewOk = oRes.status === 'fulfilled';
      const statusOk = stRes.status === 'fulfilled';
      if (overviewOk) {
        setOverview(oRes.value);
      } else if (!statusOk) {
        setOverview(null);
      }
      if (statusOk) {
        setStatus(stRes.value);
      } else if (!overviewOk) {
        setStatus(null);
      }

      if (!overviewOk && !statusOk) {
        const oMsg = oRes.status === 'rejected' ? String(oRes.reason?.message || oRes.reason || 'overview_failed') : '';
        const sMsg = stRes.status === 'rejected' ? String(stRes.reason?.message || stRes.reason || 'status_failed') : '';
        throw new Error(`Dojo endpoints unavailable. overview: ${oMsg} · status: ${sMsg}`);
      }

      if (!overviewOk || !statusOk) {
        const now = Date.now();
        if (now - lastPartialRefreshWarnRef.current > 60000) {
          lastPartialRefreshWarnRef.current = now;
          const which = !overviewOk ? 'overview' : 'status';
          const msg =
            !overviewOk && oRes.status === 'rejected'
              ? String(oRes.reason?.message || '')
              : !statusOk && stRes.status === 'rejected'
              ? String(stRes.reason?.message || '')
              : '';
          toast.message(`Partial update: ${which} endpoint slow/unavailable`, {
            description: msg.slice(0, 180) || 'Using latest available data from other endpoint.',
          });
        }
      }
      if (context?.customer_id) {
        const [lo, pq] = await Promise.all([
          api.get(`/api/learning/${context.customer_id}/overview`, { timeoutMs: 20000 }).catch(() => null),
          api.get(`/api/learning/${context.customer_id}/policy/queue`, { timeoutMs: 20000 }).catch(() => null),
        ]);
        setLearning(lo);
        setPolicyQueue(pq);
      } else {
        setLearning(null);
        setPolicyQueue(null);
      }
      void loadOrchestratorLogs({ silent: true });
      void loadAertagoControlConfig({ silent: true });
      void probeGatewayHealth(controlProtocol, { silent: true });
      void loadBlueSnapshot({ silent: true });
    } catch (e) {
      const msg = e.message || 'Failed to load AERTAGO';
      setError(msg);
      setOverview(null);
      setStatus(null);
      setControlConfig(null);
      toast.error(msg);
    } finally {
      setLoadingMain(false);
    }
  }, [
    context?.customer_id,
    controlProtocol,
    loadAertagoControlConfig,
    loadBlueSnapshot,
    loadOrchestratorLogs,
    probeGatewayHealth,
  ]);

  const loadSnapshot = useCallback(async () => {
    setLoadingSnapshot(true);
    try {
      const s = await api.get('/api/dojo-lab/snapshot', { timeoutMs: REQ_SNAPSHOT_MS });
      setSnapshot(s);
      toast.success('Technical JSON loaded — you can copy or download.');
    } catch (e) {
      setSnapshot(null);
      toast.error(e.message || 'Failed to load full snapshot');
    } finally {
      setLoadingSnapshot(false);
    }
  }, []);

  useEffect(() => {
    refreshMain();
  }, [refreshMain]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(() => {
      refreshMain();
    }, 30000);
    return () => window.clearInterval(id);
  }, [autoRefresh, refreshMain]);

  useEffect(() => {
    if (!liveOrchestratorTail || !status) return undefined;
    const id = window.setInterval(() => {
      void loadOrchestratorLogs({ silent: true });
    }, 8000);
    return () => window.clearInterval(id);
  }, [liveOrchestratorTail, status, loadOrchestratorLogs]);

  const startRangeEngine = useCallback(async () => {
    try {
      const r = await api.post('/api/dojo-lab/start', {}, { timeoutMs: 20000 });
      if (r?.started) toast.success('Range engine started.');
      else toast.message('Start requested', { description: r?.reason || 'Check diagnostics for details.' });
      await refreshMain();
    } catch (e) {
      toast.error(e.message || 'Could not start range engine');
    }
  }, [refreshMain]);

  const persistAllowlistTargets = useCallback(
    async (targets, { successToast = true } = {}) => {
      const cleaned = targets.map((x) => String(x).trim()).filter(Boolean);
      setControlBusy(true);
      try {
        const res = await api.post('/api/dojo-lab/aertago-control/allowlist', { targets: cleaned }, { timeoutMs: 20000 });
        if (successToast) {
          toast.success(`Allowlist updated (${res?.allowlist_targets?.length ?? cleaned.length} targets)`);
        }
        await loadAertagoControlConfig({ silent: true });
        return true;
      } catch (e) {
        toast.error(e.message || 'Could not update allowlist');
        return false;
      } finally {
        setControlBusy(false);
      }
    },
    [loadAertagoControlConfig],
  );

  const saveAllowlist = useCallback(async () => {
    const targets = String(controlTargetInput || '')
      .split('\n')
      .map((x) => x.trim())
      .filter(Boolean);
    await persistAllowlistTargets(targets, { successToast: true });
  }, [controlTargetInput, persistAllowlistTargets]);

  const autoScanAndAllowlist = useCallback(async () => {
    const classifyFailureCode = (msg, cfg) => {
      const text = String(msg || '').toLowerCase();
      const connectorState = cfg?.connector_state || {};
      const routeStatus = String(connectorState?.route_status || '').toLowerCase();
      if (text.includes('disabled')) return 'scan_disabled';
      if (text.includes('timed out') || text.includes('timeout')) return 'timeout';
      if (text.includes('no connector') || text.includes('connector')) return 'no_connector';
      if (text.includes('route') || routeStatus.includes('blocked') || routeStatus.includes('degraded')) return 'route_blocked';
      if (!connectorState?.active_connector_id) return 'no_connector';
      return 'route_blocked';
    };

    const liveConnectorStatus = connectorStatusLabel(controlConfig?.connector_state || null);
    const needsConnector = controlConfig?.connector_required !== false;
    if (liveConnectorStatus !== 'online') {
      const code = 'no_connector';
      const message = needsConnector
        ? 'Lab connector is required but offline. Start aerta/lab-connector on this operator PC (on lab Wi‑Fi).'
        : 'No online connector available for auto-scan.';
      setAutoScanSummary(`Auto-scan failed: ${message}`);
      setAutoScanDiagnostics({ code, message, ts: new Date().toISOString() });
      setGatewayHealth(null);
      setApprovalId('');
      setActiveSessionId('');
      toast.error(message);
      return;
    }

    setAutoScanBusy(true);
    setAutoScanSummary('');
    setAutoScanDiagnostics(null);
    setAutoScanJobVisual(null);
    try {
      const queued = await api.post('/api/dojo-lab/aertago-control/scan-now', {}, { timeoutMs: 20000 });
      const jobId = queued?.job_id;
      if (!jobId) throw new Error('Scan queue response missing job id.');

      const targetsLabel =
        Array.isArray(queued?.targets) && queued.targets.length > 0
          ? queued.targets.join(', ')
          : 'Lab scope';
      setAutoScanJobVisual({
        jobId,
        status: queued?.status || 'queued',
        targetsLabel,
        includeNoPorts: true,
        startedAt: Date.now(),
        errorText: null,
      });

      let finalStatus = null;
      for (let i = 0; i < 45; i += 1) {
        const s = await api.get(`/api/dojo-lab/aertago-control/scan-jobs/${jobId}`, { timeoutMs: 20000 });
        finalStatus = s;
        setAutoScanJobVisual((prev) =>
          prev && prev.jobId === jobId
            ? {
                ...prev,
                status: s?.status || prev.status,
                errorText: s?.error_text ?? prev.errorText,
              }
            : prev
        );
        if (s?.status === 'completed' || s?.status === 'failed') break;
        // Short polling loop for a single operator-initiated scan.
        // eslint-disable-next-line no-await-in-loop
        await wait(2000);
      }
      if (!finalStatus || finalStatus?.status !== 'completed') {
        throw new Error(finalStatus?.error_text || 'Network scan did not complete in time.');
      }

      const out = finalStatus;
      const ips = Array.from(
        new Set(
          (Array.isArray(out?.devices) ? out.devices : [])
            .map((d) => String(d?.ip || '').trim())
            .filter(Boolean)
        )
      );
      if (ips.length === 0) {
        const targetCidr = Array.isArray(out?.targets_json) ? out.targets_json[0] : '';
        setAutoScanSummary(`Scan finished for ${targetCidr || 'lab scope'}, but no hosts were discovered.`);
        toast.message('Auto-scan completed with no discovered hosts.');
        return;
      }

      const targetCidr = Array.isArray(out?.targets_json) ? out.targets_json[0] : '';
      setControlTargetInput(ips.join('\n'));
      setControlTarget(ips[0] || '');
      setAutoScanSummary(`Auto-scanned ${targetCidr || 'lab scope'}; discovered ${ips.length} host(s); allowlist synced.`);
      toast.success(`Connector scan found ${ips.length} host(s) and synced allowlist.`);
      setAutoScanJobVisual((prev) =>
        prev && prev.jobId === jobId ? { ...prev, status: 'completed', errorText: null } : prev
      );
      // Persist before reload so config fetch does not wipe unsaved discovery IPs.
      await persistAllowlistTargets(ips, { successToast: false });
    } catch (e) {
      const msg = e.message || 'Auto-scan failed';
      const code = classifyFailureCode(msg, controlConfig);
      setAutoScanSummary(`Auto-scan failed: ${msg}`);
      setAutoScanDiagnostics({ code, message: msg, ts: new Date().toISOString() });
      setAutoScanJobVisual((prev) =>
        prev ? { ...prev, status: 'failed', errorText: msg } : null
      );
      // On failure, clear stale diagnostics and session intent so operator can re-evaluate route/connectivity.
      setGatewayHealth(null);
      setApprovalId('');
      setActiveSessionId('');
      toast.error(msg);
    } finally {
      setAutoScanBusy(false);
    }
  }, [controlConfig, persistAllowlistTargets]);

  /** Remote-start connector on this PC (localhost launcher) then full cloud bootstrap. */
  const runAutomatedLabSetup = useCallback(async () => {
    setLabBootstrapBusy(true);
    setLabBootstrapSteps(null);
    setAutoScanSummary('');
    setAutoScanDiagnostics(null);
    const steps = [];
    try {
      const wake = await wakeLocalConnector();
      steps.push({
        id: 'local_launcher',
        ok: wake.ok,
        started: wake.started,
        already_running: wake.already_running,
        error: wake.ok ? null : wake.message || launcherInstallHint(),
      });
      if (wake.ok) {
        for (let i = 0; i < 24; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const cfg = await api.get('/api/dojo-lab/aertago-control/config', { timeoutMs: 15000 });
          setControlConfig(cfg);
          if (connectorStatusLabel(cfg?.connector_state) === 'online') {
            steps.push({ id: 'connector_online', ok: true });
            break;
          }
          if (i === 23) {
            steps.push({
              id: 'connector_online',
              ok: false,
              error: 'Connector did not register in time after remote start.',
            });
          }
          // eslint-disable-next-line no-await-in-loop
          await wait(1500);
        }
      }

      const name = String(experimentName || '').trim() || 'Automated lab session';
      const res = await api.post(
        '/api/dojo-lab/aertago-control/bootstrap-lab',
        {
          experiment_name: name,
          wait_for_scan_sec: 120,
          start_exercise_run: true,
          refresh_gateway: true,
          run_discovery_scan: true,
          probe_gateway: true,
        },
        { timeoutMs: 190000 }
      );
      const cloudSteps = Array.isArray(res?.steps) ? res.steps : [];
      const allSteps = [...steps, ...cloudSteps];
      setLabBootstrapSteps(allSteps);
      const cfg = res?.control_config;
      if (cfg) {
        setControlConfig(cfg);
        setGatewayRdpUrl(String(cfg?.gateway_urls?.rdp || ''));
        setGatewayVncUrl(String(cfg?.gateway_urls?.vnc || ''));
        setGatewayRdpProbeUrl(String(cfg?.gateway_probe_urls?.rdp || ''));
        setGatewayVncProbeUrl(String(cfg?.gateway_probe_urls?.vnc || ''));
        const targets = Array.isArray(cfg?.allowlist_targets) ? cfg.allowlist_targets : [];
        setControlTargetInput(targets.join('\n'));
        setControlTarget((prev) => (prev ? prev : targets[0] || ''));
      } else {
        await loadAertagoControlConfig({ silent: true });
      }
      const waitStep = allSteps.find((s) => s.id === 'discovery_scan_wait');
      const scanStep = allSteps.find((s) => s.id === 'discovery_scan');
      const launcherStep = allSteps.find((s) => s.id === 'local_launcher');
      if (launcherStep && !launcherStep.ok) {
        setAutoScanSummary(launcherStep.error || launcherInstallHint());
        toast.error('Install the local launcher once on this PC, then retry Automate lab setup.');
        return;
      }
      if (waitStep?.ok && (waitStep.devices_found ?? 0) > 0) {
        setAutoScanSummary(
          `Automated setup: discovered ${waitStep.devices_found} host(s); allowlist synced.`
        );
        toast.success(`Lab bootstrap complete — ${waitStep.devices_found} host(s) on allowlist.`);
      } else if (scanStep && !scanStep.ok) {
        const err =
          typeof scanStep.error === 'string'
            ? scanStep.error
            : scanStep.error?.message || 'Connector offline — start lab-connector on this operator PC.';
        setAutoScanSummary(`Automated setup: ${err}`);
        toast.error(err);
      } else if (res?.ok) {
        toast.success('Lab bootstrap finished.');
        setAutoScanSummary('Automated setup finished. Review gateway health and approve a target for viewer.');
      } else {
        toast.message('Lab bootstrap finished with warnings — see steps below.');
        setAutoScanSummary('Automated setup finished with warnings — expand bootstrap steps.');
      }
      const ghStep = allSteps.find((s) => s.id === 'gateway_health');
      if (ghStep) {
        setGatewayHealth({
          reachable: Boolean(ghStep.reachable),
          reason: ghStep.reason || (ghStep.ok ? 'ok' : 'unreachable'),
        });
      }
    } catch (e) {
      const msg = e.message || 'Automated lab setup failed';
      setAutoScanSummary(msg);
      toast.error(msg);
    } finally {
      setLabBootstrapBusy(false);
    }
  }, [experimentName, loadAertagoControlConfig]);

  const exportDemoPacket = useCallback(async () => {
    if (!context?.customer_id) {
      toast.error('No customer scope for export.');
      return;
    }
    try {
      const data = await api.get(
        `/api/dojo-lab/aertago-control/demo-export?customer_id=${encodeURIComponent(context.customer_id)}`,
        { timeoutMs: 90000 }
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `aertago-demo-packet-${context.customer_id}-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Demo packet downloaded');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  }, [context?.customer_id]);

  const startExerciseRun = useCallback(async () => {
    setControlBusy(true);
    try {
      const name = String(experimentName || '').trim();
      if (!name) {
        toast.error('Enter an experiment name for this run.');
        return;
      }
      const visualJobId = `run-start-${Date.now()}`;
      setRunStartVisual({
        status: 'queued',
        jobId: visualJobId,
        targetsLabel: name,
        includeNoPorts: false,
        startedAt: Date.now(),
        errorText: null,
      });
      if (runStartVisualTimerRef.current) window.clearTimeout(runStartVisualTimerRef.current);
      runStartVisualTimerRef.current = window.setTimeout(() => {
        setRunStartVisual((prev) => (prev && prev.jobId === visualJobId ? { ...prev, status: 'running' } : prev));
      }, 700);
      const body = { experiment_name: name, force_new: false };
      const tb = Number.parseInt(String(timeboxMinutes || '').trim(), 10);
      if (Number.isFinite(tb) && tb > 0) body.timebox_minutes = tb;
      const res = await api.post('/api/dojo-lab/aertago-control/run/start', body, { timeoutMs: 120000 });
      if (!res?.ok) {
        setRunStartVisual((prev) =>
          prev
            ? {
                ...prev,
                status: 'failed',
                errorText: res?.reason || 'Could not start exercise run',
              }
            : prev
        );
        toast.error(res?.reason || 'Could not start exercise run');
        return;
      }
      setRunStartVisual((prev) =>
        prev
          ? {
              ...prev,
              status: 'completed',
              jobId: String(res?.exercise_run?.run_id || prev.jobId),
              errorText: null,
            }
          : prev
      );
      toast.success(res?.reused ? 'Continuing active exercise run' : 'Exercise run started');
      await loadAertagoControlConfig({ silent: true });
      await loadBlueSnapshot({ silent: true });
      if (runStartVisualTimerRef.current) window.clearTimeout(runStartVisualTimerRef.current);
      runStartVisualTimerRef.current = window.setTimeout(() => setRunStartVisual(null), 6000);
    } catch (e) {
      const msg = e.message || 'Exercise run start failed';
      setRunStartVisual((prev) =>
        prev
          ? {
              ...prev,
              status: 'failed',
              errorText: msg,
            }
          : prev
      );
      toast.error(msg);
    } finally {
      setControlBusy(false);
    }
  }, [experimentName, loadAertagoControlConfig, loadBlueSnapshot, timeboxMinutes]);

  const endExerciseRun = useCallback(async () => {
    setControlBusy(true);
    try {
      const res = await api.post(
        '/api/dojo-lab/aertago-control/run/end',
        { note: runEndNote || undefined },
        { timeoutMs: 60000 }
      );
      if (!res?.ok) {
        toast.error(res?.reason || 'Could not end exercise run');
        return;
      }
      toast.success('Exercise run ended');
      setRunEndNote('');
      await loadAertagoControlConfig({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Exercise run end failed');
    } finally {
      setControlBusy(false);
    }
  }, [loadAertagoControlConfig, runEndNote]);

  const emergencyStopLab = useCallback(async () => {
    if (
      !window.confirm(
        'Emergency stop: cancel queued lab connector commands and close all active viewer sessions. Continue?'
      )
    ) {
      return;
    }
    setControlBusy(true);
    try {
      const res = await api.post('/api/dojo-lab/aertago-control/emergency-stop', {}, { timeoutMs: 20000 });
      if (!res?.ok) {
        toast.error('Emergency stop failed');
        return;
      }
      toast.success(
        `Stopped ${res?.viewer_sessions_stopped ?? 0} viewer(s); cancelled ${res?.cancelled_connector_commands ?? 0} queued command(s).`
      );
      if (res?.orchestrator_note) {
        toast.message('Orchestrator', { description: String(res.orchestrator_note).slice(0, 280) });
      }
      setApprovalId('');
      setActiveSessionId('');
      await loadAertagoControlConfig({ silent: true });
      await loadBlueSnapshot({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Emergency stop failed');
    } finally {
      setControlBusy(false);
    }
  }, [loadAertagoControlConfig, loadBlueSnapshot]);

  const exportRunBundle = useCallback(async () => {
    if (!context?.customer_id) {
      toast.error('No customer scope for export.');
      return;
    }
    try {
      const data = await api.get(
        `/api/dojo-lab/aertago-control/run-bundle?customer_id=${encodeURIComponent(context.customer_id)}`,
        { timeoutMs: 30000 }
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const rid = data?.exercise_run?.run_id || 'norun';
      a.download = `aertago-run-bundle-${rid}-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Run bundle downloaded');
    } catch (e) {
      toast.error(e.message || 'Run bundle export failed');
    }
  }, [context?.customer_id]);

  const loadExperimentReadiness = useCallback(async () => {
    setExperimentGateBusy(true);
    try {
      const r = await api.get('/api/dojo-lab/experiments/readiness', { timeoutMs: 20000 });
      setExperimentReadiness(r);
      toast.success('Experiment readiness updated');
    } catch (e) {
      toast.error(e.message || 'Readiness failed');
    } finally {
      setExperimentGateBusy(false);
    }
  }, []);

  const validateExperimentContract = useCallback(async () => {
    let contract;
    try {
      contract = JSON.parse(experimentContractText);
    } catch {
      toast.error('Contract must be valid JSON');
      return;
    }
    setExperimentGateBusy(true);
    try {
      const r = await api.post(
        '/api/dojo-lab/experiments/contract/validate',
        { contract },
        { timeoutMs: 20000 }
      );
      setExperimentValidateResult(r);
      toast.success(r.ok ? 'All gates passed' : 'Gates evaluated (see violations)');
    } catch (e) {
      setExperimentValidateResult(null);
      toast.error(e.message || 'Validate failed');
    } finally {
      setExperimentGateBusy(false);
    }
  }, [experimentContractText]);

  const runExperimentHarness = useCallback(
    async (dryRun) => {
      let contract;
      try {
        contract = JSON.parse(experimentContractText);
      } catch {
        toast.error('Contract must be valid JSON');
        return;
      }
      if (!context?.customer_id) {
        toast.error('No customer scope for harness.');
        return;
      }
      setExperimentGateBusy(true);
      try {
        const r = await api.post(
          `/api/dojo-lab/experiments/run/${encodeURIComponent(context.customer_id)}`,
          { contract, dry_run: dryRun },
          { timeoutMs: 30000 }
        );
        setExperimentHarnessResult(r);
        if (r.written && r.report?.run_id) {
          setExperimentEvalRunId(String(r.report.run_id));
          toast.success(`Report written (${r.report.run_id})`);
        } else {
          toast.success(dryRun ? 'Dry run complete' : 'Harness finished');
        }
      } catch (e) {
        setExperimentHarnessResult(null);
        toast.error(e.message || 'Harness failed');
      } finally {
        setExperimentGateBusy(false);
      }
    },
    [context?.customer_id, experimentContractText]
  );

  const compareExperimentEvaluations = useCallback(async () => {
    if (!context?.customer_id) {
      toast.error('No customer scope for compare.');
      return;
    }
    const a = experimentCompareRunA.trim();
    const b = experimentCompareRunB.trim();
    if (!a || !b) {
      toast.error('Enter two experiment run IDs');
      return;
    }
    setExperimentGateBusy(true);
    try {
      const body = { run_id_a: a, run_id_b: b };
      const fa = experimentCompareFileA.trim();
      const fb = experimentCompareFileB.trim();
      if (fa) body.evaluation_file_a = fa;
      if (fb) body.evaluation_file_b = fb;
      const r = await api.post(
        `/api/dojo-lab/experiments/compare-evaluations/${encodeURIComponent(context.customer_id)}`,
        body,
        { timeoutMs: 20000 }
      );
      setExperimentCompareResult(r);
      toast.success('Comparison ready');
    } catch (e) {
      setExperimentCompareResult(null);
      toast.error(e.message || 'Compare failed');
    } finally {
      setExperimentGateBusy(false);
    }
  }, [
    context?.customer_id,
    experimentCompareRunA,
    experimentCompareRunB,
    experimentCompareFileA,
    experimentCompareFileB,
  ]);

  const appendExperimentEvaluation = useCallback(async () => {
    if (!context?.customer_id) {
      toast.error('No customer scope for evaluation append.');
      return;
    }
    const runId = experimentEvalRunId.trim();
    if (!runId) {
      toast.error('Enter experiment run ID (use Write report, or paste a run_id).');
      return;
    }
    let metrics;
    try {
      metrics = JSON.parse(experimentEvalMetricsText);
    } catch {
      toast.error('Metrics must be valid JSON object');
      return;
    }
    if (!metrics || typeof metrics !== 'object' || Array.isArray(metrics)) {
      toast.error('Metrics must be a JSON object');
      return;
    }
    setExperimentGateBusy(true);
    try {
      const r = await api.post(
        `/api/dojo-lab/experiments/runs/${encodeURIComponent(context.customer_id)}/${encodeURIComponent(runId)}/evaluate`,
        { metrics, label: experimentEvalLabel.trim() || undefined },
        { timeoutMs: 20000 }
      );
      setExperimentEvalLastWritten(r.written || null);
      toast.success('Evaluation metrics appended');
    } catch (e) {
      setExperimentEvalLastWritten(null);
      toast.error(e.message || 'Append evaluation failed');
    } finally {
      setExperimentGateBusy(false);
    }
  }, [context?.customer_id, experimentEvalLabel, experimentEvalMetricsText, experimentEvalRunId]);

  const applyExerciseNameToExperimentContract = useCallback(() => {
    const raw = String(experimentName || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '.')
      .replace(/^\.+|\.+$/g, '');
    const id = (raw && /^[a-z0-9]/.test(raw) ? raw : `lab.${raw || 'run'}`).slice(0, 200);
    try {
      const obj = JSON.parse(experimentContractText);
      if (obj && typeof obj === 'object') {
        obj.experiment_id = id;
        setExperimentContractText(JSON.stringify(obj, null, 2));
        toast.message('experiment_id updated', { description: id });
      }
    } catch {
      toast.error('Fix JSON before applying exercise name');
    }
  }, [experimentName, experimentContractText]);

  const loadResearchSession = useCallback(async () => {
    const sid = researchRsSessionId.trim();
    if (!sid || !context?.customer_id) return;
    setResearchRsBusy(true);
    try {
      const r = await api.get(
        `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions/${encodeURIComponent(sid)}`,
        { timeoutMs: 20000 }
      );
      setResearchRsSession(r);
    } catch (e) {
      toast.error(e.message || 'Could not load research session');
    } finally {
      setResearchRsBusy(false);
    }
  }, [context?.customer_id, researchRsSessionId]);

  const loadResearchPlannerStatus = useCallback(async () => {
    setResearchRsBusy(true);
    try {
      const r = await api.get('/api/dojo-lab/research-sessions/planner/status', { timeoutMs: 15000 });
      setResearchRsPlannerStatus(r);
      toast.success('Planner status updated');
    } catch (e) {
      toast.error(e.message || 'Planner status failed');
    } finally {
      setResearchRsBusy(false);
    }
  }, []);

  const createResearchSession = useCallback(async () => {
    if (!context?.customer_id) {
      toast.error('No customer scope.');
      return;
    }
    const targets = researchRsTargets
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!targets.length) {
      toast.error('Enter at least one allowlisted lab target IP or hostname.');
      return;
    }
    setResearchRsBusy(true);
    try {
      const r = await api.post(
        `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions`,
        {
          goal: researchRsGoal.trim(),
          targets,
          experiment_name: researchRsExperimentName.trim(),
          protocol: controlProtocol === 'vnc' ? 'vnc' : 'rdp',
          control_plane:
            researchRsControlPlane === 'oob'
              ? 'oob'
              : researchRsControlPlane === 'auto'
              ? 'auto'
              : 'aertago',
        },
        { timeoutMs: 25000 }
      );
      const sid = r.session?.session_id;
      if (sid) setResearchRsSessionId(sid);
      setResearchRsSession(r.session || null);
      toast.success('Red AI research session created — execute steps (optional approve when AERTAGO_SUPERVISED_MODE=true).');
    } catch (e) {
      toast.error(e.message || 'Create session failed');
    } finally {
      setResearchRsBusy(false);
    }
  }, [
    context?.customer_id,
    controlProtocol,
    researchRsControlPlane,
    researchRsExperimentName,
    researchRsGoal,
    researchRsTargets,
  ]);

  const loadResearchTargetDiscovery = useCallback(
    async (opts = {}) => {
      if (!context?.customer_id) {
        setResearchTargetRows([]);
        return;
      }
      const silent = opts.silent === true;
      setResearchTargetBusy(true);
      try {
        const protocol = controlProtocol === 'vnc' ? 'vnc' : 'rdp';
        const r = await api.get(
          `/api/dojo-lab/oob/discovery/${encodeURIComponent(context.customer_id)}?protocol=${encodeURIComponent(protocol)}`,
          { timeoutMs: 25000 }
        );
        const rows = Array.isArray(r?.rows) ? r.rows : [];
        setResearchTargetRows(rows);
        if (!silent) toast.success(`Target access table refreshed (${rows.length})`);
      } catch (e) {
        if (!silent) toast.error(e.message || 'Could not refresh target access table');
        setResearchTargetRows((prev) => prev || []);
      } finally {
        setResearchTargetBusy(false);
      }
    },
    [context?.customer_id, controlProtocol]
  );

  const chooseResearchTargetFromRow = useCallback((row) => {
    const target = String(row?.target || '').trim();
    if (!target) return;
    setResearchRsTargets(target);
    setControlTarget(target);
    toast.success(`Target selected: ${target}`);
  }, []);

  const createResearchAutoSessionFromRow = useCallback(
    async (row) => {
      if (!context?.customer_id) {
        toast.error('No customer scope.');
        return;
      }
      const target = String(row?.target || '').trim();
      if (!target) {
        toast.error('This row has no target identifier.');
        return;
      }
      setResearchRsBusy(true);
      try {
        const r = await api.post(
          `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions`,
          {
            goal: researchRsGoal.trim(),
            targets: [target],
            experiment_name: researchRsExperimentName.trim(),
            protocol: controlProtocol === 'vnc' ? 'vnc' : 'rdp',
            control_plane: 'auto',
          },
          { timeoutMs: 25000 }
        );
        const sid = r.session?.session_id;
        if (sid) setResearchRsSessionId(sid);
        setResearchRsSession(r.session || null);
        setResearchRsTargets(target);
        setControlTarget(target);
        toast.success(`Auto research session created for ${target}`);
      } catch (e) {
        toast.error(e.message || 'Create auto session failed');
      } finally {
        setResearchRsBusy(false);
      }
    },
    [context?.customer_id, controlProtocol, researchRsExperimentName, researchRsGoal]
  );

  const approveResearchStep = useCallback(
    async (stepId) => {
      if (!context?.customer_id || !researchRsSessionId.trim()) return;
      setResearchRsBusy(true);
      try {
        await api.post(
          `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions/${encodeURIComponent(
            researchRsSessionId.trim()
          )}/steps/${encodeURIComponent(stepId)}/approve`,
          { note: researchRsStepNote.trim() || undefined },
          { timeoutMs: 20000 }
        );
        toast.success(`Approved ${stepId}`);
        await loadResearchSession();
      } catch (e) {
        toast.error(e.message || 'Approve failed');
      } finally {
        setResearchRsBusy(false);
      }
    },
    [context?.customer_id, loadResearchSession, researchRsSessionId, researchRsStepNote]
  );

  const executeResearchStep = useCallback(
    async (stepId) => {
      if (!context?.customer_id || !researchRsSessionId.trim()) return;
      setResearchRsBusy(true);
      try {
        const r = await api.post(
          `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions/${encodeURIComponent(
            researchRsSessionId.trim()
          )}/steps/${encodeURIComponent(stepId)}/execute`,
          {},
          { timeoutMs: 60000 }
        );
        setResearchRsSession(r.session || null);
        if (r.result?.session?.viewer_url) {
          toast.message('Viewer URL issued', { description: 'Use Open viewer below or Live Control.' });
        } else {
          toast.success(`Executed ${stepId}`);
        }
      } catch (e) {
        toast.error(e.message || 'Execute failed');
      } finally {
        setResearchRsBusy(false);
      }
    },
    [context?.customer_id, researchRsSessionId]
  );

  const proposeResearchStrategyAttempt = useCallback(async () => {
    if (!context?.customer_id || !researchRsSessionId.trim()) {
      toast.error('Create or load a research session first.');
      return;
    }
    const title = researchStrategyTitle.trim();
    if (!title) {
      toast.error('Strategy title is required.');
      return;
    }
    let evidenceInputs = {};
    try {
      evidenceInputs = JSON.parse(researchStrategyEvidenceText || '{}');
      if (!evidenceInputs || typeof evidenceInputs !== 'object' || Array.isArray(evidenceInputs)) {
        throw new Error('invalid');
      }
    } catch {
      toast.error('Evidence JSON must be a valid object.');
      return;
    }
    setResearchRsBusy(true);
    try {
      const r = await api.post(
        `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions/${encodeURIComponent(
          researchRsSessionId.trim()
        )}/strategy-attempts/propose`,
        {
          title,
          method: researchStrategyMethod.trim() || 'approved_safe_path',
          target: researchStrategyTarget.trim() || undefined,
          rationale: researchStrategyRationale.trim() || undefined,
          evidence_inputs: evidenceInputs,
        },
        { timeoutMs: 20000 }
      );
      setResearchRsSession(r.session || null);
      setResearchStrategyTitle('');
      toast.success('Strategy attempt proposed (pending approval).');
    } catch (e) {
      toast.error(e.message || 'Could not propose strategy attempt');
    } finally {
      setResearchRsBusy(false);
    }
  }, [
    context?.customer_id,
    researchRsSessionId,
    researchStrategyEvidenceText,
    researchStrategyMethod,
    researchStrategyRationale,
    researchStrategyTarget,
    researchStrategyTitle,
  ]);

  const approveResearchStrategyAttempt = useCallback(
    async (attemptId) => {
      if (!context?.customer_id || !researchRsSessionId.trim()) return;
      setResearchRsBusy(true);
      try {
        const r = await api.post(
          `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions/${encodeURIComponent(
            researchRsSessionId.trim()
          )}/strategy-attempts/${encodeURIComponent(attemptId)}/approve`,
          { note: researchStrategyNote.trim() || undefined },
          { timeoutMs: 20000 }
        );
        setResearchRsSession(r.session || null);
        toast.success(`Approved strategy ${attemptId}`);
      } catch (e) {
        toast.error(e.message || 'Strategy approval failed');
      } finally {
        setResearchRsBusy(false);
      }
    },
    [context?.customer_id, researchRsSessionId, researchStrategyNote]
  );

  const executeResearchStrategyAttempt = useCallback(
    async (attemptId) => {
      if (!context?.customer_id || !researchRsSessionId.trim()) return;
      let executionEvidence = {};
      try {
        executionEvidence = JSON.parse(researchStrategyEvidenceText || '{}');
        if (!executionEvidence || typeof executionEvidence !== 'object' || Array.isArray(executionEvidence)) {
          throw new Error('invalid');
        }
      } catch {
        toast.error('Evidence JSON must be a valid object.');
        return;
      }
      setResearchRsBusy(true);
      try {
        const r = await api.post(
          `/api/dojo-lab/research-sessions/${encodeURIComponent(context.customer_id)}/sessions/${encodeURIComponent(
            researchRsSessionId.trim()
          )}/strategy-attempts/${encodeURIComponent(attemptId)}/execute`,
          {
            outcome: researchStrategyOutcome === 'succeeded' ? 'succeeded' : researchStrategyOutcome === 'blocked' ? 'blocked' : 'failed',
            note: researchStrategyNote.trim() || undefined,
            evidence: executionEvidence,
          },
          { timeoutMs: 20000 }
        );
        setResearchRsSession(r.session || null);
        toast.success(`Executed strategy ${attemptId}`);
      } catch (e) {
        toast.error(e.message || 'Strategy execute failed');
      } finally {
        setResearchRsBusy(false);
      }
    },
    [context?.customer_id, researchRsSessionId, researchStrategyEvidenceText, researchStrategyNote, researchStrategyOutcome]
  );

  const approveViewerSession = useCallback(async () => {
    setControlBusy(true);
    try {
      if (!controlTarget) {
        toast.error('Pick a target from the allowlist first.');
        return;
      }
      const res = await api.post(
        '/api/dojo-lab/aertago-control/approve-viewer',
        {
          target: controlTarget,
          protocol: controlProtocol,
          reason: controlReason,
        },
        { timeoutMs: 20000 }
      );
      if (!res?.ok) {
        toast.error(res?.reason || 'Approval failed');
        return;
      }
      setApprovalId(res?.approval?.id || '');
      toast.success(`Approved ${controlTarget} (${controlProtocol.toUpperCase()})`);
      await loadAertagoControlConfig({ silent: true });
      void loadBlueSnapshot({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Could not approve session');
    } finally {
      setControlBusy(false);
    }
  }, [controlProtocol, controlReason, controlTarget, loadAertagoControlConfig, loadBlueSnapshot]);

  const startViewerSession = useCallback(async () => {
    setControlBusy(true);
    try {
      if (!approvalId) {
        toast.error('Approve a session first.');
        return;
      }
      const res = await api.post(
        '/api/dojo-lab/aertago-control/session/start',
        {
          approval_id: approvalId,
          target: controlTarget || undefined,
          protocol: controlProtocol || undefined,
        },
        { timeoutMs: 20000 }
      );
      if (!res?.ok) {
        toast.error(res?.reason || 'Session start failed');
        return;
      }
      if (res?.session?.id) setActiveSessionId(res.session.id);
      toast.success('Viewer session started — click inside the desktop frame before typing.');
      await loadAertagoControlConfig({ silent: true });
      void loadBlueSnapshot({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Could not start viewer session');
    } finally {
      setControlBusy(false);
    }
  }, [approvalId, controlProtocol, controlTarget, loadAertagoControlConfig, loadBlueSnapshot]);

  const openViewerForRedAiReportedSuccess = useCallback(async () => {
    if (!controlTarget) {
      toast.error('Pick a target from the allowlist first.');
      return;
    }
    setControlBusy(true);
    try {
      // Ensure we have a fresh approval id (so the flow is fully connected even if user didn’t click Approve).
      let aid = approvalId;
      if (!aid) {
        const appr = await api.post(
          '/api/dojo-lab/aertago-control/approve-viewer',
          { target: controlTarget, protocol: controlProtocol, reason: controlReason },
          { timeoutMs: 20000 }
        );
        if (!appr?.ok || !appr?.approval?.id) {
          toast.error(appr?.reason || 'Approval failed');
          return;
        }
        aid = String(appr.approval.id);
        setApprovalId(aid);
      }

      const started = await api.post(
        '/api/dojo-lab/aertago-control/session/start',
        { approval_id: aid, target: controlTarget || undefined, protocol: controlProtocol || undefined },
        { timeoutMs: 20000 }
      );
      if (!started?.ok) {
        toast.error(started?.reason || 'Session start failed');
        return;
      }
      if (started?.session?.id) setActiveSessionId(started.session.id);
      setViewerFocusArmed(true);
      toast.success('Viewer session started (Red AI reported success) — click inside the desktop frame before typing.');
      await loadAertagoControlConfig({ silent: true });
      void loadBlueSnapshot({ silent: true });

      // Smoothly scroll the viewer into view.
      try {
        viewerSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      } catch {
        /* ignore */
      }
    } catch (e) {
      toast.error(e.message || 'Could not open viewer session');
    } finally {
      setControlBusy(false);
    }
  }, [
    approvalId,
    controlProtocol,
    controlReason,
    controlTarget,
    loadAertagoControlConfig,
    loadBlueSnapshot,
  ]);

  useEffect(() => {
    // Best-effort: prefill Red AI scan scope from the API host’s visible subnets.
    // (If your API is cloud-hosted, it will not see your lab Wi‑Fi unless deployed inside it or connected via VPN/tunnel.)
    if (!context?.customer_id) return;
    if (redAiScanTargets.trim()) return;
    api
      .get('/api/network-scan/detect-subnets')
      .then((r) => {
        if (!r?.recommended_cidr) return;
        setRedAiScanTargets(String(r.recommended_cidr));
      })
      .catch(() => null);
  }, [context?.customer_id, redAiScanTargets]);

  const queueRedAiNetworkScan = useCallback(async () => {
    if (!context?.customer_id) {
      toast.error('Missing customer_id — cannot queue scan.');
      return;
    }
    const tks = redAiScanTargets
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (tks.length === 0) {
      toast.error('Enter a scan range first (e.g. 192.168.1.0/24).');
      return;
    }
    const portList = redAiScanPorts
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    setRedAiScanBusy(true);
    try {
      const res = await api.post(`/api/network-scan/${encodeURIComponent(context.customer_id)}/jobs`, {
        targets: tks,
        ports: portList.length ? portList : undefined,
        auto_ingest: true,
        include_no_ports: Boolean(redAiScanIncludeNoPorts),
      });
      if (networkScanJob?.registerScan && res?.job_id) {
        networkScanJob.registerScan({
          jobId: res.job_id,
          customerId: context.customer_id,
          scanStartedAt: Date.now(),
          targetsLabel: tks.join(', '),
          includeNoPorts: Boolean(redAiScanIncludeNoPorts),
          initialJob: {
            job_id: res.job_id,
            status: 'queued',
            targets_json: tks,
            ports_json: portList.length ? portList : [],
          },
          devices: null,
        });
      }
      toast.success(`Network scan queued: ${res?.job_id || 'job created'}`);
    } catch (e) {
      toast.error(e?.message || 'Queue failed');
    } finally {
      setRedAiScanBusy(false);
    }
  }, [
    context?.customer_id,
    networkScanJob,
    redAiScanIncludeNoPorts,
    redAiScanPorts,
    redAiScanTargets,
  ]);

  useEffect(() => {
    const t = networkScanJob?.tracked;
    if (!t?.jobId || t.job?.status !== 'completed') return;
    if (!Array.isArray(t.devices)) return;
    if (t.devices.length === 0) {
      lastNetScanAllowlistSyncJobRef.current = t.jobId;
      return;
    }
    if (lastNetScanAllowlistSyncJobRef.current === t.jobId) return;

    const ips = [...new Set(t.devices.map((d) => String(d?.ip || '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
    if (!ips.length) {
      lastNetScanAllowlistSyncJobRef.current = t.jobId;
      return;
    }
    lastNetScanAllowlistSyncJobRef.current = t.jobId;

    const existing = String(controlTargetInput || '')
      .split('\n')
      .map((x) => x.trim())
      .filter(Boolean);
    const merged = [...new Set([...existing, ...ips])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    void (async () => {
      const ok = await persistAllowlistTargets(merged, { successToast: false });
      if (ok) {
        setControlTarget((prev) => (prev && merged.includes(prev) ? prev : ips[0]));
        toast.success(`Network scan finished — merged ${ips.length} host(s); selected ${ips[0]} for AERTAGO.`);
      } else {
        lastNetScanAllowlistSyncJobRef.current = '';
      }
    })();
  }, [networkScanJob?.tracked, controlTargetInput, persistAllowlistTargets]);

  useEffect(() => {
    // Re-arm focus overlay when the active viewer session changes.
    if (activeSessionId) setViewerFocusArmed(true);
  }, [activeSessionId]);

  const stopViewerSession = useCallback(async (sessionId = activeSessionId) => {
    if (!sessionId) return;
    setControlBusy(true);
    try {
      const res = await api.post(
        '/api/dojo-lab/aertago-control/session/stop',
        { session_id: sessionId },
        { timeoutMs: 20000 }
      );
      if (!res?.ok) {
        toast.error(res?.reason || 'Session stop failed');
        return;
      }
      setActiveSessionId((prev) => (prev === sessionId ? '' : prev));
      toast.success('Viewer session stopped');
      await loadAertagoControlConfig({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Could not stop viewer session');
    } finally {
      setControlBusy(false);
    }
  }, [activeSessionId, loadAertagoControlConfig]);

  const startAllAllowlistedSessions = useCallback(async () => {
    const targets = Array.isArray(controlConfig?.allowlist_targets) ? controlConfig.allowlist_targets : [];
    if (targets.length === 0) {
      toast.error('Allowlist is empty.');
      return;
    }
    setControlBusy(true);
    let okCount = 0;
    try {
      for (const t of targets) {
        // Explicit operator action still required: this button is the single explicit batch action.
        const approval = await api.post(
          '/api/dojo-lab/aertago-control/approve-viewer',
          { target: t, protocol: controlProtocol, reason: controlReason },
          { timeoutMs: 20000 }
        );
        if (!approval?.ok || !approval?.approval?.id) continue;
        const started = await api.post(
          '/api/dojo-lab/aertago-control/session/start',
          { approval_id: approval.approval.id, target: t, protocol: controlProtocol },
          { timeoutMs: 20000 }
        );
        if (started?.ok) {
          okCount += 1;
          if (!activeSessionId && started?.session?.id) setActiveSessionId(started.session.id);
        }
      }
      toast.success(`Started ${okCount}/${targets.length} sessions (${String(controlProtocol).toUpperCase()})`);
      await loadAertagoControlConfig({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Batch session start failed');
    } finally {
      setControlBusy(false);
    }
  }, [activeSessionId, controlConfig?.allowlist_targets, controlProtocol, controlReason, loadAertagoControlConfig]);

  useEffect(() => {
    if (!controlProtocol) return;
    void probeGatewayHealth(controlProtocol, { silent: true });
  }, [controlProtocol, probeGatewayHealth]);

  useEffect(() => {
    // Connect the Red AI UI scaffold to the live viewer: when the run transitions to "reported_success",
    // automatically open a viewer session (approve + start) if one isn't already active.
    if (redAiUiStatus !== 'reported_success') return;
    if (!controlTarget) return;
    if (activeSessionId) return;
    const key = `${controlTarget}:${controlProtocol}`;
    if (redAiAutoOpenGuardRef.current === key) return;
    redAiAutoOpenGuardRef.current = key;
    void openViewerForRedAiReportedSuccess();
  }, [activeSessionId, controlProtocol, controlTarget, openViewerForRedAiReportedSuccess, redAiUiStatus]);

  useEffect(() => {
    if (!context?.customer_id) return undefined;
    void loadBlueSnapshot({ silent: true });
    const id = window.setInterval(() => {
      void loadBlueSnapshot({ silent: true });
    }, 25000);
    return () => window.clearInterval(id);
  }, [context?.customer_id, loadBlueSnapshot]);

  useEffect(() => {
    const id = window.setInterval(() => setBlueSnapNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    return () => {
      if (runStartVisualTimerRef.current) {
        window.clearTimeout(runStartVisualTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const rem = Array.isArray(blueSnap?.blue_computed?.prioritized_remediations)
      ? blueSnap.blue_computed.prioritized_remediations.slice(0, 6).map((r) => `${r?.id || r?.title || ''}:${r?.why || ''}`)
      : [];
    const risk = blueSnap?.blue_computed?.risk_posture || {};
    const summary = String(blueSnap?.blue_computed?.summary_text || '').trim();
    const sig = JSON.stringify({
      critical: Number(risk?.critical ?? 0),
      high: Number(risk?.high ?? 0),
      rem,
      summary,
    });
    const tsLabel = new Date().toLocaleTimeString();
    const prev = blueSnapPrevSigRef.current;
    if (!blueSnap || !blueSnap.blue_computed) {
      setBlueSnapEventLine(`latest: no blue snapshot data (${tsLabel})`);
      return;
    }
    if (!prev) {
      setBlueSnapEventLine(`latest: blue snapshot loaded (${tsLabel})`);
      blueSnapPrevSigRef.current = sig;
      return;
    }
    if (prev !== sig) {
      const prevObj = (() => {
        try {
          return JSON.parse(prev);
        } catch {
          return {};
        }
      })();
      const riskChanged = prevObj.critical !== Number(risk?.critical ?? 0) || prevObj.high !== Number(risk?.high ?? 0);
      const remChanged = JSON.stringify(prevObj.rem || []) !== JSON.stringify(rem);
      if (riskChanged) {
        setBlueSnapEventLine(`latest: blue risk recomputed (${tsLabel})`);
      } else if (remChanged) {
        setBlueSnapEventLine(`latest: remediations changed (${tsLabel})`);
      } else {
        setBlueSnapEventLine(`latest: blue snapshot updated (${tsLabel})`);
      }
      blueSnapPrevSigRef.current = sig;
      return;
    }
    setBlueSnapEventLine(`latest: no change (${tsLabel})`);
  }, [blueSnap]);

  useEffect(() => {
    if (!context?.customer_id) return;
    void loadResearchTargetDiscovery({ silent: true });
  }, [context?.customer_id, controlProtocol, loadResearchTargetDiscovery]);

  const approveRecommendation = useCallback(
    async (id) => {
      if (!context?.customer_id) return;
      setRecBusyId(`approve:${id}`);
      try {
        await api.post(`/api/learning/${context.customer_id}/recommendations/${id}/approve`, {
          note: 'Approved from AERTAGO',
        });
        toast.success('Recommendation approved');
        await refreshMain();
      } catch (e) {
        toast.error(e.message || 'Approve failed');
      } finally {
        setRecBusyId(null);
      }
    },
    [context?.customer_id, refreshMain]
  );

  const promoteRecommendation = useCallback(
    async (id) => {
      if (!context?.customer_id) return;
      setRecBusyId(`promote:${id}`);
      try {
        await api.post(`/api/learning/${context.customer_id}/recommendations/${id}/promote`, {
          note: 'Promoted from AERTAGO',
        });
        toast.success('Recommendation promoted to pipeline artifact');
        await refreshMain();
      } catch (e) {
        toast.error(e.message || 'Promote failed');
      } finally {
        setRecBusyId(null);
      }
    },
    [context?.customer_id, refreshMain]
  );

  const executeRecommendation = useCallback(
    async (id) => {
      if (!context?.customer_id) return;
      setRecBusyId(`execute:${id}`);
      try {
        await api.post(`/api/learning/${context.customer_id}/recommendations/${id}/execute`, {
          note: 'Executed from AERTAGO',
        });
        toast.success('Recommendation executed into pipeline task');
        await refreshMain();
      } catch (e) {
        toast.error(e.message || 'Execute failed');
      } finally {
        setRecBusyId(null);
      }
    },
    [context?.customer_id, refreshMain]
  );

  const runPolicyGate = useCallback(async () => {
    if (!context?.customer_id) return;
    setRecBusyId('policy:run');
    try {
      const r = await api.post(`/api/learning/${context.customer_id}/policy/run`, {
        note: 'Run from AERTAGO',
      });
      toast.success('Policy gate run complete', {
        description: `Processed ${r?.processed || 0}, queued ${r?.queued_for_approval || 0}, auto-applied ${r?.auto_applied || 0}.`,
      });
      await refreshMain();
    } catch (e) {
      toast.error(e.message || 'Policy gate run failed');
    } finally {
      setRecBusyId(null);
    }
  }, [context?.customer_id, refreshMain]);

  const approvePolicyQueueItem = useCallback(
    async (itemId) => {
      if (!context?.customer_id) return;
      setRecBusyId(`queue-approve:${itemId}`);
      try {
        await api.post(`/api/learning/${context.customer_id}/policy/queue/${itemId}/approve`, {
          note: 'Approved from AERTAGO policy queue',
        });
        toast.success('Policy queue item approved');
        await refreshMain();
      } catch (e) {
        toast.error(e.message || 'Queue approve failed');
      } finally {
        setRecBusyId(null);
      }
    },
    [context?.customer_id, refreshMain]
  );

  const rejectPolicyQueueItem = useCallback(
    async (itemId) => {
      if (!context?.customer_id) return;
      setRecBusyId(`queue-reject:${itemId}`);
      try {
        await api.post(`/api/learning/${context.customer_id}/policy/queue/${itemId}/reject`, {
          note: 'Rejected from AERTAGO policy queue',
        });
        toast.success('Policy queue item rejected');
        await refreshMain();
      } catch (e) {
        toast.error(e.message || 'Queue reject failed');
      } finally {
        setRecBusyId(null);
      }
    },
    [context?.customer_id, refreshMain]
  );

  const copyRunbook = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  const snapshotText = snapshot ? JSON.stringify(snapshot, null, 2) : '';
  const dockerSocketComposeSnippet = [
    "services:",
    "  api:",
    "    volumes:",
    "      - /var/run/docker.sock:/var/run/docker.sock",
    "    environment:",
    "      - DOCKER_HOST=unix:///var/run/docker.sock",
  ].join('\n');

  const downloadSnapshot = () => {
    if (!snapshotText) return;
    const blob = new Blob([snapshotText], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cyber-range-full-export-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Download started');
  };

  const copySnapshot = async () => {
    if (!snapshotText) return;
    try {
      await navigator.clipboard.writeText(snapshotText);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  const cm = status?.current_match;
  const summary = cm?.summary;
  const recent = status?.recent_matches;
  const scen = status?.scenarios;
  const evo = status?.evolution_memory;
  const pools = status?.evolution_pools;
  const cumR = pools?.cumulative?.red_ai;
  const cumB = pools?.cumulative?.blue_ai;
  const matchAnalytics = useMemo(() => buildMatchAnalytics(recent?.recent), [recent?.recent]);
  const { trendData, chrono, outcomeCounts, allWinRatesFlat } = matchAnalytics;
  const outcomeBarData = useMemo(
    () => [
      { name: 'Red AI', wins: outcomeCounts.red, fill: '#fb7185' },
      { name: 'Blue AI', wins: outcomeCounts.blue, fill: '#38bdf8' },
      { name: 'Draws', wins: outcomeCounts.draw, fill: '#a1a1aa' },
    ],
    [outcomeCounts]
  );
  const evolutionWinRates =
    cumR?.win_rate != null || cumB?.win_rate != null
      ? [
          {
            name: 'Red (evolution)',
            rate: cumR?.win_rate != null ? Math.round(Number(cumR.win_rate) * 1000) / 10 : 0,
            fill: '#fb7185',
          },
          {
            name: 'Blue (evolution)',
            rate: cumB?.win_rate != null ? Math.round(Number(cumB.win_rate) * 1000) / 10 : 0,
            fill: '#38bdf8',
          },
        ]
      : [];

  const ort = status?.orchestrator_runtime;
  const orchestratorLastLine = lastNonEmptyLogLine(logPayload?.text);
  const rs = ort?.summary;
  const docker = ort?.docker;
  const needsDockerSocketHelp = docker?.docker_cli === false;
  const proc = ort?.process;
  const art = ort?.artifact_activity;
  const hints = overview?.operator_hints;
  const aertagoCaps = status?.aertago_capabilities ?? overview?.aertago_capabilities;
  const activeSessions = useMemo(
    () => (Array.isArray(controlConfig?.sessions_active) ? controlConfig.sessions_active : []),
    [controlConfig?.sessions_active]
  );
  const connectorState = controlConfig?.connector_state || null;
  const connectorRequired = controlConfig?.connector_required !== false;
  const connectorStatus = connectorStatusLabel(connectorState);
  const connectorStatusUi = connectorStatusUiLabel(connectorStatus, connectorRequired);
  const connectorSeenAgeLabel = connectorState?.last_seen_at_utc
    ? formatAgeSeconds(Math.round((Date.now() - Date.parse(connectorState.last_seen_at_utc)) / 1000))
    : 'never';
  const scanControl = controlConfig?.scan_control || null;
  const blueSnapCheckedTs = useMemo(() => {
    const raw = blueSnap?.checked_at || blueSnap?.blue_computed?.computed_at_utc || null;
    if (!raw) return null;
    const ts = Date.parse(String(raw));
    return Number.isNaN(ts) ? null : ts;
  }, [blueSnap?.checked_at, blueSnap?.blue_computed?.computed_at_utc]);
  const blueSnapAgeSec = blueSnapCheckedTs != null ? Math.max(0, Math.floor((blueSnapNowMs - blueSnapCheckedTs) / 1000)) : null;
  const blueSnapNextRefreshSec = blueSnapAgeSec == null ? null : Math.max(0, 25 - (blueSnapAgeSec % 25));
  const blueSnapCyclePct = blueSnapAgeSec == null ? 0 : Math.min(100, ((blueSnapAgeSec % 25) / 25) * 100);
  const currentViewerSession =
    activeSessions.find((s) => s?.id && s.id === activeSessionId) || activeSessions[0] || null;
  const rangeDescription = showDiagnostics
    ? (ort?.note ||
      'The dashboard does not embed the orchestrator; it reads shared JSON and optionally probes Docker or host processes.')
    : 'AERTAGO surfaces live Red/Blue lab artifacts from the shared dojo folder (read-only for operators).';

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-2 text-primary">
            <Target className="h-6 w-6" />
            <span className="font-heading text-sm font-semibold uppercase tracking-wide">AERTAGO</span>
            <Badge variant="outline" className="border-primary/30 text-[10px] font-normal text-muted-foreground">
              Controlled lab · Red/Blue
            </Badge>
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">AERTAGO</h1>
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground/90">
            Cyber Range — isolated environment only
          </p>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            This page mixes <strong className="text-foreground">readiness</strong>,{' '}
            <strong className="text-foreground">formal gates / research sessions</strong>,{' '}
            <strong className="text-foreground">live lab control + Red AI agent</strong>, and{' '}
            <strong className="text-foreground">match telemetry</strong>. Use the strip below to jump — work top to bottom
            on first visit. Offensive tooling and RDP/VNC apply only in your authorized lab network.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="rounded border-border"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (30s)
          </label>
          <Button type="button" variant="default" size="sm" onClick={refreshMain} disabled={loadingMain} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loadingMain ? 'animate-spin' : ''}`} />
            Refresh status
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setShowDiagnostics((v) => !v)}>
            {showDiagnostics ? 'Hide diagnostics' : 'Show diagnostics'}
          </Button>
        </div>
      </div>

      <nav
        aria-label="Cyber Range page sections"
        className="flex flex-wrap gap-1 rounded-xl border border-border/70 bg-muted/20 p-1.5 text-[11px] shadow-sm"
      >
        <a
          href="#cyber-range-readiness"
          className="rounded-lg px-2.5 py-1.5 font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          1 · Readiness
        </a>
        <a
          href="#cyber-range-gates-research"
          className="rounded-lg px-2.5 py-1.5 font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          2 · Gates &amp; research
        </a>
        <a
          href="#cyber-range-lab-workflow"
          className="rounded-lg px-2.5 py-1.5 font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          3 · Live lab + Red AI
        </a>
        <a
          href="#cyber-range-telemetry"
          className="rounded-lg px-2.5 py-1.5 font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          4 · Matches &amp; learning
        </a>
      </nav>

      <section id="cyber-range-readiness" className="scroll-mt-24 space-y-6">
        <p className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">Section 1 — Readiness</span> · Deployment flags and the end-to-end
          lab story. Use it to see whether bridges and files are present before you invest time in the controls below.
        </p>
      {aertagoCaps ? (
        <Card className="border-border/90 bg-card/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-primary" />
              Deployment readiness
              <Badge variant={aertagoReadinessBadgeVariant(aertagoCaps.readiness)}>
                {String(aertagoCaps.readiness || 'unknown')}
              </Badge>
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">{aertagoCaps.summary}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-xs sm:grid-cols-2">
            <ul className="space-y-1.5 text-muted-foreground">
              <li>
                <span className="font-medium text-foreground">Orchestrator script</span> —{' '}
                {aertagoCaps.orchestrator_script_present ? 'present' : 'missing'}
              </li>
              <li>
                <span className="font-medium text-foreground">Range engine live</span> —{' '}
                {aertagoCaps.range_engine_live ? 'yes' : 'no'}
              </li>
              <li>
                <span className="font-medium text-foreground">Lab artifacts fresh</span> —{' '}
                {aertagoCaps.lab_artifacts_fresh ? 'yes' : 'no'}
              </li>
              <li>
                <span className="font-medium text-foreground">Match history</span> —{' '}
                {aertagoCaps.match_history_available ? 'on disk' : 'none yet'}
              </li>
            </ul>
            <ul className="space-y-1.5 text-muted-foreground">
              <li>
                <span className="font-medium text-foreground">Events JSON files</span> — {aertagoCaps.events_json_files ?? '—'}
              </li>
              <li>
                <span className="font-medium text-foreground">Live execution</span> —{' '}
                {aertagoCaps.live_execution_enabled ? 'enabled' : 'off'}{' '}
                <span className="text-[10px] text-muted-foreground/80">
                  ({aertagoCaps.live_execution_source || '—'})
                </span>
              </li>
              <li>
                <span className="font-medium text-foreground">Metasploit bridge</span> —{' '}
                {aertagoCaps.metasploit_bridge ? 'on' : 'off'}{' '}
                <span className="text-[10px] text-muted-foreground/80">
                  ({aertagoCaps.metasploit_bridge_source || '—'})
                </span>
              </li>
              <li>
                <span className="font-medium text-foreground">Remote desktop bridge</span> —{' '}
                {aertagoCaps.remote_desktop_bridge ? 'on' : 'off'}{' '}
                <span className="text-[10px] text-muted-foreground/80">
                  ({aertagoCaps.remote_desktop_bridge_source || '—'})
                </span>
              </li>
              <li>
                <span className="font-medium text-foreground">Pentest executor</span> —{' '}
                {aertagoCaps.pentest_executor_enabled ? 'enabled' : 'off'}
              </li>
              <li>
                <span className="font-medium text-foreground">Kali bridge URL+token</span> —{' '}
                {aertagoCaps.kali_bridge_configured ? 'configured' : 'not set'}
              </li>
            </ul>
            <p className="sm:col-span-2 text-[11px] leading-relaxed text-muted-foreground/90">
              Unset <code className="rounded bg-muted px-1">AERTAGO_*</code> bridges infer from{' '}
              <code className="rounded bg-muted px-1">AERTA_PENTEST_EXECUTOR_ENABLED</code> + scan + Kali bridge,{' '}
              <code className="rounded bg-muted px-1">AERTA_AERTAX_MCP_METASPLOIT_URL</code>, and{' '}
              <code className="rounded bg-muted px-1">AERTAGO_RDP_GATEWAY_URL</code> /{' '}
              <code className="rounded bg-muted px-1">AERTAGO_VNC_URL</code> /{' '}
              <code className="rounded bg-muted px-1">AERTA_NOVNC_URL</code>. Set an{' '}
              <code className="rounded bg-muted px-1">AERTAGO_*</code> variable explicitly to force on or off.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Card className="border-border/80 bg-muted/10">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <ListOrdered className="h-4 w-4 text-primary" />
            AERTAGO flow
          </CardTitle>
          <CardDescription>
            End-to-end narrative for your controlled lab: discovery through retest. Execution and remote control apply only
            to machines you own in an isolated segment (wired or Wi‑Fi).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-3 pl-5 text-sm text-muted-foreground">
            {AERTAGO_FLOW_STEPS.map((step) => (
              <li key={step.title} className="leading-snug">
                <span className="font-medium text-foreground">{step.title}</span>
                <span className="text-muted-foreground"> — {step.detail}</span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>
      </section>

      <section id="cyber-range-gates-research" className="scroll-mt-24 space-y-6">
        <p className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">Section 2 — Gates &amp; research</span> · Immutable harness
          reports and brokered Red AI sessions. When a step needs a live desktop, it still depends on{' '}
          <a href="#cyber-range-lab-workflow" className="text-primary underline-offset-2 hover:underline">
            allowlist + gateway
          </a>{' '}
          from section 3.
        </p>
      <Card className="border-border/80 bg-muted/10">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <FileCode className="h-4 w-4 text-primary" />
            Experiment contract (gates & harness)
          </CardTitle>
          <CardDescription>
            Validate formal gates, record an immutable harness report under{' '}
            <code className="rounded bg-muted px-1">tenants/…/experiments/runs/</code>, and compare evaluation metrics
            between two runs. See <code className="rounded bg-muted px-1">aerta/SETUP.md</code> §10d.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-xs text-muted-foreground">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={loadExperimentReadiness}
              disabled={experimentGateBusy}
            >
              <RefreshCw className={`mr-1 h-3 w-3 ${experimentGateBusy ? 'animate-spin' : ''}`} />
              Readiness
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={validateExperimentContract} disabled={experimentGateBusy}>
              Validate contract
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => runExperimentHarness(true)} disabled={experimentGateBusy}>
              Dry-run harness
            </Button>
            <Button type="button" size="sm" onClick={() => runExperimentHarness(false)} disabled={experimentGateBusy}>
              Write report
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={applyExerciseNameToExperimentContract} disabled={experimentGateBusy}>
              Use exercise name as experiment_id
            </Button>
          </div>
          {experimentReadiness ? (
            <div className="rounded-md border border-border/60 bg-background/50 p-2 font-mono text-[10px] leading-relaxed text-muted-foreground">
              <div>
                lab mode:{' '}
                <span className="text-foreground">{experimentReadiness.research_lab?.mode ?? '—'}</span> · scan:{' '}
                {String(experimentReadiness.research_lab?.scan_enabled)} · allowlist rules:{' '}
                {experimentReadiness.research_lab?.allowlist_rule_count ?? '—'}
              </div>
              <div>
                gate context — exercise_run:{' '}
                <span className="text-foreground">{String(experimentReadiness.gate_context?.exercise_run_active)}</span>{' '}
                · gateway URL set:{' '}
                <span className="text-foreground">{String(experimentReadiness.gateway_url_configured)}</span>
              </div>
            </div>
          ) : (
            <p className="text-[11px]">Click Readiness to load research-lab snapshot and gate context.</p>
          )}
          {experimentValidateResult ? (
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <Badge variant={experimentValidateResult.ok ? 'default' : 'destructive'}>
                {experimentValidateResult.ok ? 'gates ok' : 'violations'}
              </Badge>
              <span>
                {(experimentValidateResult.violations && experimentValidateResult.violations.length) || 0} issue(s)
              </span>
            </div>
          ) : null}
          <label className="block text-[11px] font-medium text-foreground">
            Contract JSON
            <textarea
              className="mt-1 min-h-[160px] w-full resize-y rounded-md border border-border bg-background p-2 font-mono text-[11px] text-foreground"
              value={experimentContractText}
              onChange={(e) => setExperimentContractText(e.target.value)}
              spellCheck={false}
            />
          </label>
          {experimentHarnessResult?.report ? (
            <p className="text-[11px]">
              Last harness:{' '}
              <code className="rounded bg-muted px-1">{experimentHarnessResult.report.run_id}</code>
              {experimentHarnessResult.written ? (
                <>
                  {' '}
                  → <code className="rounded bg-muted px-1 text-[10px]">{experimentHarnessResult.written}</code>
                </>
              ) : (
                ' (dry run — no file)'
              )}
              {' · '}
              passed: <span className="text-foreground">{String(experimentHarnessResult.report.passed)}</span>
            </p>
          ) : null}
          <div className="border-t border-border/50 pt-3">
            <p className="mb-2 text-[11px] font-medium text-foreground">Append evaluation metrics</p>
            <p className="mb-2 text-[10px] leading-relaxed">
              Writes a timestamped JSON file under{' '}
              <code className="rounded bg-muted px-1">experiments/runs/…/evaluations/</code> for harness/compare workflows.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-[11px] sm:col-span-2">
                Experiment run ID
                <div className="mt-1 flex flex-wrap gap-2">
                  <input
                    className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
                    value={experimentEvalRunId}
                    onChange={(e) => setExperimentEvalRunId(e.target.value)}
                    placeholder="filled after Write report, or paste run_id"
                  />
                  {experimentHarnessResult?.report?.run_id ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="shrink-0"
                      onClick={() => setExperimentEvalRunId(String(experimentHarnessResult.report.run_id))}
                      disabled={experimentGateBusy}
                    >
                      Use last harness
                    </Button>
                  ) : null}
                </div>
              </label>
              <label className="text-[11px]">
                Label (optional)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={experimentEvalLabel}
                  onChange={(e) => setExperimentEvalLabel(e.target.value)}
                  placeholder="e.g. post-run manual score"
                />
              </label>
              <div className="flex items-end">
                <Button type="button" size="sm" onClick={appendExperimentEvaluation} disabled={experimentGateBusy}>
                  Append metrics
                </Button>
              </div>
            </div>
            <label className="mt-2 block text-[11px] font-medium text-foreground">
              Metrics JSON (object)
              <textarea
                className="mt-1 min-h-[88px] w-full resize-y rounded-md border border-border bg-background p-2 font-mono text-[11px] text-foreground"
                value={experimentEvalMetricsText}
                onChange={(e) => setExperimentEvalMetricsText(e.target.value)}
                spellCheck={false}
              />
            </label>
            {experimentEvalLastWritten ? (
              <p className="mt-1 text-[10px] text-muted-foreground">
                Last write: <code className="rounded bg-muted px-1 break-all">{experimentEvalLastWritten}</code>
              </p>
            ) : null}
          </div>
          <div className="border-t border-border/50 pt-3">
            <p className="mb-2 text-[11px] font-medium text-foreground">Compare evaluation metrics (two runs)</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-[11px]">
                Run ID A
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
                  value={experimentCompareRunA}
                  onChange={(e) => setExperimentCompareRunA(e.target.value)}
                  placeholder="uuid from Write report"
                />
              </label>
              <label className="text-[11px]">
                Run ID B
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
                  value={experimentCompareRunB}
                  onChange={(e) => setExperimentCompareRunB(e.target.value)}
                />
              </label>
              <label className="text-[11px]">
                Eval file A (optional)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[10px] text-foreground"
                  value={experimentCompareFileA}
                  onChange={(e) => setExperimentCompareFileA(e.target.value)}
                  placeholder="default: latest in run A"
                />
              </label>
              <label className="text-[11px]">
                Eval file B (optional)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[10px] text-foreground"
                  value={experimentCompareFileB}
                  onChange={(e) => setExperimentCompareFileB(e.target.value)}
                  placeholder="default: latest in run B"
                />
              </label>
            </div>
            <Button
              type="button"
              size="sm"
              className="mt-2"
              variant="outline"
              onClick={compareExperimentEvaluations}
              disabled={experimentGateBusy}
            >
              Compare evaluations
            </Button>
            {Array.isArray(experimentCompareResult?.comparison) && experimentCompareResult.comparison.length > 0 ? (
              <div className="mt-2 max-h-48 overflow-auto rounded-md border border-border/60 bg-background/60">
                <table className="w-full border-collapse text-left text-[10px]">
                  <thead>
                    <tr className="border-b border-border/60 text-muted-foreground">
                      <th className="p-1.5 font-medium">metric</th>
                      <th className="p-1.5 font-medium">A</th>
                      <th className="p-1.5 font-medium">B</th>
                      <th className="p-1.5 font-medium">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {experimentCompareResult.comparison.map((row) => (
                      <tr key={row.key} className="border-b border-border/40">
                        <td className="p-1.5 font-mono text-foreground">{row.key}</td>
                        <td className="max-w-[120px] truncate p-1.5 font-mono">{JSON.stringify(row.left)}</td>
                        <td className="max-w-[120px] truncate p-1.5 font-mono">{JSON.stringify(row.right)}</td>
                        <td className="p-1.5 font-mono text-foreground">
                          {row.numeric_diff != null ? row.numeric_diff : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/80 bg-muted/10">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <BookOpen className="h-4 w-4 text-primary" />
            Red AI research session (instrumented + broker)
          </CardTitle>
          <CardDescription>
            Deterministic plan: instrument → exercise run → viewer approve → session start. Each step needs explicit
            approval before execute. Targets must match your AERTAGO allowlist; gateway env must be set for the final
            step.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-xs text-muted-foreground">
          <div className="space-y-2 rounded-md border border-border/60 bg-background/40 p-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] font-medium text-foreground">Target access table (scan -> approved path)</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[10px]"
                onClick={() => loadResearchTargetDiscovery()}
                disabled={researchTargetBusy}
              >
                {researchTargetBusy ? 'Refreshing...' : 'Refresh discovery'}
              </Button>
            </div>
            <div className="max-h-52 overflow-auto rounded-md border border-border/50 bg-background/70">
              <table className="w-full border-collapse text-left text-[10px]">
                <thead>
                  <tr className="border-b border-border/60 text-muted-foreground">
                    <th className="p-1.5 font-medium">target</th>
                    <th className="p-1.5 font-medium">type</th>
                    <th className="p-1.5 font-medium">recommended path</th>
                    <th className="p-1.5 font-medium">actions</th>
                  </tr>
                </thead>
                <tbody>
                  {researchTargetRows.map((row) => (
                    <tr key={`${row.target}-${row.protocol}`} className="border-b border-border/30 last:border-b-0">
                      <td className="max-w-[170px] truncate p-1.5 font-mono text-foreground">{row.target || '—'}</td>
                      <td className="p-1.5">{row.device_type || 'unknown'}</td>
                      <td className="p-1.5">
                        <Badge variant={accessPathBadgeVariant(row.recommended_access_path)}>
                          {row.recommended_access_path || 'unknown'}
                        </Badge>
                        {row.blocked_reason ? (
                          <p className="mt-1 max-w-[220px] truncate text-[9px] text-muted-foreground">{row.blocked_reason}</p>
                        ) : null}
                      </td>
                      <td className="p-1.5">
                        <div className="flex flex-wrap gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => chooseResearchTargetFromRow(row)}
                          >
                            Use target
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="default"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => createResearchAutoSessionFromRow(row)}
                            disabled={researchRsBusy || !row.target}
                          >
                            Create auto session
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {researchTargetRows.length === 0 ? (
                    <tr>
                      <td className="p-2 text-[10px] text-muted-foreground" colSpan={4}>
                        No discovery rows yet. Run discovery in the lab, then click Refresh discovery.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="sm:col-span-2 text-[11px] font-medium text-foreground">
              Goal
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                value={researchRsGoal}
                onChange={(e) => setResearchRsGoal(e.target.value)}
              />
            </label>
            <label className="text-[11px] font-medium text-foreground">
              Allowlisted targets (comma or newline)
              <textarea
                className="mt-1 min-h-[56px] w-full rounded-md border border-border bg-background p-2 font-mono text-[11px] text-foreground"
                value={researchRsTargets}
                onChange={(e) => setResearchRsTargets(e.target.value)}
                placeholder={
                  researchRsControlPlane === 'oob'
                    ? 'oob-asset-id-1'
                    : researchRsControlPlane === 'auto'
                    ? controlTarget || '192.168.1.50 or oob asset id'
                    : controlTarget || '192.168.1.50'
                }
                spellCheck={false}
              />
            </label>
            <label className="text-[11px] font-medium text-foreground">
              Exercise name
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                value={researchRsExperimentName}
                onChange={(e) => setResearchRsExperimentName(e.target.value)}
              />
            </label>
            <label className="text-[11px] font-medium text-foreground">
              Control plane
              <select
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                value={researchRsControlPlane}
                onChange={(e) => setResearchRsControlPlane(e.target.value)}
              >
                <option value="auto">Auto-select approved access path</option>
                <option value="aertago">AERTAGO (allowlist + gateway)</option>
                <option value="oob">OOB hardware (AMT/iLO/iDRAC/IPMI/KVM)</option>
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={createResearchSession} disabled={researchRsBusy}>
              Create research session
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={loadResearchPlannerStatus} disabled={researchRsBusy}>
              Planner status
            </Button>
            <input
              className="min-w-[200px] flex-1 rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
              value={researchRsSessionId}
              onChange={(e) => setResearchRsSessionId(e.target.value)}
              placeholder="session id (rs-…)"
            />
            <Button type="button" size="sm" variant="outline" onClick={loadResearchSession} disabled={researchRsBusy}>
              Refresh session
            </Button>
          </div>
          {researchRsPlannerStatus ? (
            <p className="text-[10px] text-muted-foreground">
              planner {researchRsPlannerStatus.ready ? 'ready' : 'fallback'} · model{' '}
              <code className="rounded bg-muted px-1">{researchRsPlannerStatus.model || '—'}</code> · recommended free{' '}
              <code className="rounded bg-muted px-1">{researchRsPlannerStatus.recommended_free_model || 'qwen2.5:14b-instruct'}</code>
            </p>
          ) : null}
          {researchRsSession?.access_decision ? (
            <p className="text-[10px] text-muted-foreground">
              auto decision:{' '}
              <code className="rounded bg-muted px-1">{researchRsSession.access_decision.recommended_access_path}</code>
              {researchRsSession.access_decision.blocked_reason ? (
                <>
                  {' '}
                  · reason{' '}
                  <code className="rounded bg-muted px-1">{researchRsSession.access_decision.blocked_reason}</code>
                </>
              ) : null}
            </p>
          ) : null}
          <label className="block text-[11px] text-foreground">
            Optional note on next step approval
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
              value={researchRsStepNote}
              onChange={(e) => setResearchRsStepNote(e.target.value)}
              placeholder="e.g. advisor OK for step lab-2"
            />
          </label>
          <div className="rounded-md border border-border/60 bg-background/40 p-2">
            <p className="text-[11px] font-medium text-foreground">New strategy attempts (human approval required)</p>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Propose a safe, no-touch strategy; approval is required before execution. Every propose/approve/execute
              action is logged into session evidence.
            </p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <label className="text-[11px] text-foreground">
                Strategy title
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={researchStrategyTitle}
                  onChange={(e) => setResearchStrategyTitle(e.target.value)}
                  placeholder="e.g. Validate OOB aliases before relay fallback"
                />
              </label>
              <label className="text-[11px] text-foreground">
                Method tag
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
                  value={researchStrategyMethod}
                  onChange={(e) => setResearchStrategyMethod(e.target.value)}
                  placeholder="approved_safe_path"
                />
              </label>
              <label className="text-[11px] text-foreground">
                Target (optional)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
                  value={researchStrategyTarget}
                  onChange={(e) => setResearchStrategyTarget(e.target.value)}
                  placeholder="192.168.1.50"
                />
              </label>
              <label className="text-[11px] text-foreground">
                Execution outcome
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={researchStrategyOutcome}
                  onChange={(e) => setResearchStrategyOutcome(e.target.value)}
                >
                  <option value="failed">failed</option>
                  <option value="blocked">blocked</option>
                  <option value="succeeded">succeeded</option>
                </select>
              </label>
              <label className="sm:col-span-2 text-[11px] text-foreground">
                Rationale / approval note
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={researchStrategyNote}
                  onChange={(e) => setResearchStrategyNote(e.target.value)}
                  placeholder="why this strategy is safe and approved"
                />
              </label>
              <label className="sm:col-span-2 text-[11px] text-foreground">
                Evidence JSON
                <textarea
                  className="mt-1 min-h-[60px] w-full rounded-md border border-border bg-background p-2 font-mono text-[10px] text-foreground"
                  value={researchStrategyEvidenceText}
                  onChange={(e) => setResearchStrategyEvidenceText(e.target.value)}
                  spellCheck={false}
                />
              </label>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="secondary" onClick={proposeResearchStrategyAttempt} disabled={researchRsBusy}>
                Propose strategy
              </Button>
            </div>
            {Array.isArray(researchRsSession?.strategy_attempts) && researchRsSession.strategy_attempts.length > 0 ? (
              <div className="mt-2 space-y-1 rounded-md border border-border/50 bg-background/60 p-2">
                {researchRsSession.strategy_attempts.map((at) => (
                  <div key={at.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/30 pb-1 last:border-0">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[10px] text-foreground">
                        {at.id} · {at.method}
                      </p>
                      <p className="text-[11px] text-muted-foreground">{at.title}</p>
                      <p className="text-[10px] text-muted-foreground">
                        status: {at.status} · approved: {String(!!at.approved)} · evidence: {Array.isArray(at.evidence) ? at.evidence.length : 0}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-7 px-2 text-[10px]"
                        onClick={() => approveResearchStrategyAttempt(at.id)}
                        disabled={researchRsBusy || !!at.approved}
                      >
                        Approve
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className="h-7 px-2 text-[10px]"
                        onClick={() => executeResearchStrategyAttempt(at.id)}
                        disabled={researchRsBusy || !at.approved || !!at.executed_at}
                      >
                        Execute
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          {Array.isArray(researchRsSession?.plan) && researchRsSession.plan.length > 0 ? (
            <div className="space-y-2 rounded-md border border-border/60 bg-background/40 p-2">
              {researchRsSession.plan.map((st) => (
                <div
                  key={st.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-border/30 py-1.5 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] text-foreground">{st.id}</p>
                    <p className="text-[11px] text-muted-foreground">{st.title}</p>
                    <p className="text-[10px] text-muted-foreground">
                      approved: {String(!!st.approved)} · executed: {String(!!st.executed)}
                    </p>
                    {st.result?.session?.viewer_url ? (
                      <a
                        href={st.result.session.viewer_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] text-primary underline"
                      >
                        Open viewer URL
                      </a>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-7 px-2 text-[10px]"
                      onClick={() => approveResearchStep(st.id)}
                      disabled={researchRsBusy || st.approved}
                    >
                      Approve
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      className="h-7 px-2 text-[10px]"
                      onClick={() => executeResearchStep(st.id)}
                      disabled={researchRsBusy || !st.approved || st.executed}
                    >
                      Execute
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px]">Create a session to see the instrumented plan.</p>
          )}
        </CardContent>
      </Card>
      </section>

      <section id="cyber-range-lab-workflow" className="scroll-mt-24 space-y-4">
        <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/[0.07] via-transparent to-rose-500/[0.04] px-4 py-3 sm:px-5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">Step 3 — same data, two surfaces</p>
          <h2 className="mt-1 font-heading text-base font-semibold text-foreground">Live lab control and AERTAX Red AI</h2>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-muted-foreground">
            <strong className="text-foreground">Live Control</strong> is where you save the allowlist, run discovery, set
            gateways, and start the browser viewer. <strong className="text-foreground">AERTAX Red AI console</strong> reads
            that same selected target and pilot network-scan job under &quot;AERTAGO lab scope&quot; — use{' '}
            <em className="text-foreground/90">Insert into mission prompt</em> so the embedded agent is aligned with what
            you configured here, then connect and send your query.
          </p>
          <ol className="mt-2 flex list-none flex-col gap-1.5 text-[11px] text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-6">
            <li>
              <span className="font-mono text-primary">1</span> Allowlist / scan / pick target below
            </li>
            <li>
              <span className="font-mono text-primary">2</span> Approve + start viewer when ready
            </li>
            <li>
              <span className="font-mono text-primary">3</span> Optional: Red AI agent with inserted lab facts
            </li>
          </ol>
        </div>

      <Card className="border-primary/30 bg-card/60">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <Monitor className="h-4 w-4 text-primary" />
            AERTAGO Live Control (lab only)
            <Badge variant={connectorStatusBadgeVariant(connectorStatus)}>
              Connector {connectorStatusUi}
            </Badge>
          </CardTitle>
          <CardDescription>
            {connectorRequired
              ? 'Lab connector must stay online for discovery and LAN scans (hosted API cannot reach private subnets). Viewer relay still uses gateway URLs configured here — lab PCs are not modified.'
              : 'Connector-backed auto-scan is optional when AERTAGO_CONNECTOR_REQUIRED=false. Viewer access can run without connector using OOB or direct viewer relay URLs.'}{' '}
            Lab workstations are not modified by AERTAGO for remote control — gateway, allowlist, and approvals are configured from here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <AertagoConnectorSetupPanel
            connectorOnline={connectorStatus === 'online'}
            onRefreshConfig={loadAertagoControlConfig}
            onAutomateLab={runAutomatedLabSetup}
            labBootstrapBusy={labBootstrapBusy}
          />
          {connectorRequired && connectorStatus !== 'online' ? (
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-[11px] leading-relaxed text-amber-100">
              <p className="font-semibold text-amber-50">Lab connector required — currently offline</p>
              <p className="mt-1 text-amber-100/90">
                On <strong>this operator PC</strong> (the machine running your browser on lab Wi‑Fi — not on target lab machines),
                run <code className="rounded bg-muted px-1">aerta/lab-connector</code> with{' '}
                <code className="rounded bg-muted px-1">AERTA_CONNECTOR_API_BASE</code> pointing at this API and{' '}
                <code className="rounded bg-muted px-1">AERTA_CONNECTOR_REGISTRATION_KEY</code> matching the server. It speaks
                outbound to the cloud and runs discovery from your network view. Auto-scan stays blocked until heartbeats arrive.
              </p>
            </div>
          ) : null}
          {controlConfig?.viewer_control_assistant ? (
            <div className="space-y-2 rounded-lg border border-violet-500/35 bg-violet-500/[0.07] p-3 text-[11px] leading-relaxed">
              <div className="flex flex-wrap items-center gap-2">
                <Brain className="h-4 w-4 shrink-0 text-violet-300" />
                <span className="text-sm font-semibold text-foreground">Control assistant</span>
                <Badge
                  variant={
                    (controlConfig.viewer_control_assistant.readiness_score ?? 0) >= 80 ? 'default' : 'secondary'
                  }
                >
                  {controlConfig.viewer_control_assistant.readiness_score ?? 0}% ready
                </Badge>
              </div>
              <p className="text-muted-foreground">{controlConfig.viewer_control_assistant.operator_principle}</p>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-1.5 rounded-full bg-violet-500 transition-all duration-500"
                  style={{
                    width: `${Math.min(100, controlConfig.viewer_control_assistant.readiness_score ?? 0)}%`,
                  }}
                />
              </div>
              <ul className="mt-2 max-h-[220px] space-y-1.5 overflow-y-auto pr-1">
                {(controlConfig.viewer_control_assistant.next_steps || []).map((s) => (
                  <li
                    key={s.id}
                    className={`flex gap-2 rounded border px-2 py-1.5 ${
                      s.done ? 'border-emerald-500/40 bg-emerald-500/[0.06]' : 'border-border/80 bg-background/50'
                    }`}
                  >
                    <span className={s.done ? 'text-emerald-400' : 'text-muted-foreground'}>{s.done ? '✓' : '○'}</span>
                    <span>
                      <span className="font-medium text-foreground">{s.title}</span>
                      <span className="mt-0.5 block text-muted-foreground">{s.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {controlConfig?.cold_start_bootstrap ? (
            <div className="space-y-2 rounded-lg border border-cyan-500/35 bg-cyan-500/[0.06] p-3 text-[11px] leading-relaxed">
              <div className="flex flex-wrap items-center gap-2">
                <ListOrdered className="h-4 w-4 shrink-0 text-cyan-300" />
                <span className="text-sm font-semibold text-foreground">Cold start &amp; Red AI bootstrap</span>
                <Badge variant="outline" className="font-mono text-[10px]">
                  {controlConfig.cold_start_bootstrap.phase}
                </Badge>
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  className="h-7 text-[10px]"
                  disabled={controlBusy || labBootstrapBusy}
                  onClick={() => runAutomatedLabSetup()}
                >
                  {labBootstrapBusy ? 'Automating…' : 'Automate lab setup'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-7 text-[10px]"
                  disabled={coldStartBusy || labBootstrapBusy}
                  onClick={() => refreshColdStartBootstrap()}
                >
                  {coldStartBusy ? 'Refreshing…' : 'Refresh autodetect'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px]"
                  disabled={planAssistBusy}
                  onClick={() => runPlanAssist()}
                >
                  {planAssistBusy ? 'Planning…' : 'Run Red AI planner'}
                </Button>
              </div>
              <label className="block text-[10px] text-muted-foreground">
                Optional focus for the planner (e.g. “gateway 404”)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-[11px] text-foreground"
                  value={planAssistFocus}
                  onChange={(e) => setPlanAssistFocus(e.target.value)}
                  placeholder="Optional — sent with cold-start + assistant facts only"
                />
              </label>
              {planAssistText ? (
                <div className="mt-2 rounded border border-border/70 bg-background/80 p-2">
                  <p className="mb-1 text-[10px] font-medium text-muted-foreground">Red AI planner output</p>
                  <pre className="max-h-[240px] overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-foreground">
                    {planAssistText}
                  </pre>
                </div>
              ) : null}
              <p className="text-muted-foreground">{controlConfig.cold_start_bootstrap.phase_detail}</p>
              <p className="text-cyan-200/90">
                {controlConfig.cold_start_bootstrap.constraints?.narrative}
              </p>
              <p className="text-[10px] text-muted-foreground">
                <strong className="text-foreground">Automate lab setup</strong> remotely starts{' '}
                <code className="rounded bg-muted px-1">lab-connector</code> on this PC (via local launcher on port 17871), then runs
                gateway autodetect, exercise run, discovery, allowlist sync, and gateway probe in one flow. One-time install:{' '}
                <code className="rounded bg-muted px-1">install_windows_connector.ps1</code>.
              </p>
              {labBootstrapSteps?.length ? (
                <ul className="mt-1 max-h-[140px] space-y-1 overflow-y-auto rounded border border-border/60 bg-background/50 p-2 text-[10px]">
                  {labBootstrapSteps.map((s) => (
                    <li key={s.id} className={s.ok ? 'text-emerald-300' : 'text-amber-200'}>
                      {s.ok ? '✓' : '○'} {s.id}
                      {s.devices_found != null ? ` — ${s.devices_found} host(s)` : ''}
                      {s.error ? ` — ${typeof s.error === 'string' ? s.error : s.error?.message || 'failed'}` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              <ul className="mt-1 max-h-[180px] space-y-1 overflow-y-auto pr-1">
                {(controlConfig.cold_start_bootstrap.checks || []).map((c) => (
                  <li
                    key={c.id}
                    className={`flex gap-2 rounded border px-2 py-1 ${
                      c.ok ? 'border-emerald-500/35 bg-emerald-500/[0.05]' : 'border-amber-500/35 bg-amber-500/[0.05]'
                    }`}
                  >
                    <span className={c.ok ? 'text-emerald-400' : 'text-amber-300'}>{c.ok ? '✓' : '○'}</span>
                    <span>
                      <span className="font-medium text-foreground">{c.label}</span>
                      {c.optional ? (
                        <span className="ml-1 text-[10px] text-muted-foreground">(optional)</span>
                      ) : null}
                      <span className="mt-0.5 block text-muted-foreground">{c.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <details className="rounded border border-border/70 bg-background/60">
                <summary className="cursor-pointer px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  Control-plane recipes (copy on the machine that runs Docker — not on lab PCs)
                </summary>
                <div className="space-y-2 border-t border-border/50 px-2 py-2">
                  {(controlConfig.cold_start_bootstrap.control_plane_recipes || []).map((r) => (
                    <div key={r.id} className="rounded border border-border/60 bg-muted/20 p-2">
                      <p className="font-medium text-foreground">{r.title}</p>
                      {r.shell_unix ? (
                        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-[10px] text-muted-foreground">
                          {r.shell_unix}
                        </pre>
                      ) : null}
                      {Array.isArray(r.env_lines) ? (
                        <ul className="mt-1 list-disc pl-4 text-[10px] text-muted-foreground">
                          {r.env_lines.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                      {r.notes ? <p className="mt-1 text-[10px] text-muted-foreground">{r.notes}</p> : null}
                    </div>
                  ))}
                </div>
              </details>
              <details className="rounded border border-border/70 bg-background/60">
                <summary className="cursor-pointer px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  Red AI planning context (structured facts)
                </summary>
                <pre className="max-h-[200px] overflow-auto whitespace-pre-wrap break-all border-t border-border/50 p-2 text-[10px] text-muted-foreground">
                  {JSON.stringify(controlConfig.cold_start_bootstrap.red_ai_planning_context, null, 2)}
                </pre>
              </details>
            </div>
          ) : null}
          <div className="space-y-3 rounded-lg border border-primary/35 bg-primary/[0.06] p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Exercise run</span>
              {controlConfig?.exercise_run?.run_id ? (
                <Badge variant="outline" className="font-mono text-[10px]">
                  {controlConfig.exercise_run.run_id}
                </Badge>
              ) : (
                <span className="text-[11px] text-muted-foreground">no active run</span>
              )}
              {controlConfig?.exercise_run?.status === 'active' &&
              controlConfig?.exercise_run?.timebox_until_utc ? (
                <span className="text-[10px] text-amber-200">
                  timebox until {new Date(controlConfig.exercise_run.timebox_until_utc).toLocaleString()}
                </span>
              ) : null}
            </div>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Tie scans, connector commands, approvals, and viewer sessions to one <code className="rounded bg-muted px-1">run_id</code> for
              exports and lab notebooks. Scope fingerprint updates when you save the allowlist.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-[11px] text-muted-foreground">
                Experiment name
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-xs"
                  value={experimentName}
                  onChange={(e) => setExperimentName(e.target.value)}
                  placeholder="e.g. April OT segment baseline"
                />
              </label>
              <label className="text-[11px] text-muted-foreground">
                Timebox (minutes, optional)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-xs"
                  value={timeboxMinutes}
                  onChange={(e) => setTimeboxMinutes(e.target.value)}
                  placeholder="e.g. 120"
                  inputMode="numeric"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={startExerciseRun} disabled={controlBusy}>
                Start / continue run
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={endExerciseRun} disabled={controlBusy}>
                End run
              </Button>
              <label className="flex min-w-[140px] flex-1 items-end gap-1 text-[11px] text-muted-foreground">
                <span className="sr-only">End note</span>
                <input
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                  value={runEndNote}
                  onChange={(e) => setRunEndNote(e.target.value)}
                  placeholder="optional note when ending run"
                />
              </label>
              <Button type="button" size="sm" variant="secondary" onClick={exportRunBundle} disabled={controlBusy}>
                <Download className="mr-1 h-3 w-3" />
                Export run bundle
              </Button>
              <Button type="button" size="sm" variant="destructive" onClick={emergencyStopLab} disabled={controlBusy}>
                <AlertTriangle className="mr-1 h-3 w-3" />
                Emergency stop
              </Button>
            </div>
            {runStartVisual && (runStartVisual.status === 'queued' || runStartVisual.status === 'running') ? (
              <NetworkScanLive
                status={runStartVisual.status}
                jobId={runStartVisual.jobId}
                targetsLabel={runStartVisual.targetsLabel}
                includeNoPorts={runStartVisual.includeNoPorts}
                startedAt={runStartVisual.startedAt}
              />
            ) : null}
            {runStartVisual && (runStartVisual.status === 'completed' || runStartVisual.status === 'failed') ? (
              <NetworkScanTerminalBanner status={runStartVisual.status} errorText={runStartVisual.errorText} />
            ) : null}
            {controlConfig?.scope_fingerprint ? (
              <p className="text-[10px] text-muted-foreground">
                Scope fingerprint <code className="rounded bg-muted px-1">{controlConfig.scope_fingerprint}</code>
              </p>
            ) : null}
            {controlConfig?.live_status ? (
              <div className="grid gap-2 border-t border-border/50 pt-2 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded border border-border/60 bg-background/60 px-2 py-1.5 text-[10px]">
                  <span className="font-medium text-foreground">Connector queue</span>
                  <p className="text-muted-foreground">
                    {controlConfig.live_status.connector_queued_commands ?? 0} queued command(s)
                  </p>
                </div>
                <div className="rounded border border-border/60 bg-background/60 px-2 py-1.5 text-[10px]">
                  <span className="font-medium text-foreground">Viewer sessions</span>
                  <p className="text-muted-foreground">{controlConfig.live_status.active_viewer_sessions ?? 0} active</p>
                </div>
                <div className="rounded border border-border/60 bg-background/60 px-2 py-1.5 text-[10px]">
                  <span className="font-medium text-foreground">Scan</span>
                  <p className="text-muted-foreground">{controlConfig.live_status.scan_status || '—'}</p>
                </div>
                <div className="rounded border border-border/60 bg-background/60 px-2 py-1.5 text-[10px]">
                  <span className="font-medium text-foreground">Run on last scan</span>
                  <p className="font-mono text-muted-foreground">{controlConfig.live_status.scan_run_id || '—'}</p>
                </div>
              </div>
            ) : null}
            <div className="relative overflow-hidden rounded-md border border-sky-500/25 bg-sky-500/[0.06] p-2">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-sky-400/25">
                <div
                  className="h-full bg-sky-300/80 transition-all duration-1000"
                  style={{ width: `${blueSnapCyclePct}%` }}
                />
              </div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Shield className="h-4 w-4 text-sky-400" />
                <span className="text-xs font-medium text-foreground">Blue snapshot (same tenant, live lab)</span>
                {blueSnapLoading ? <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" /> : null}
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                  <span className="relative inline-flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400/70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-300" />
                  </span>
                  live
                </span>
                {blueSnapAgeSec != null ? (
                  <span className="text-[10px] text-muted-foreground">
                    updated {blueSnapAgeSec}s ago · next refresh {blueSnapNextRefreshSec}s
                  </span>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => loadBlueSnapshot({ silent: false })}
                  disabled={blueSnapLoading || !context?.customer_id}
                >
                  Refresh blue
                </Button>
              </div>
              {blueSnap?.blue_computed?.summary_text ? (
                <p className="text-[11px] leading-relaxed text-muted-foreground">{blueSnap.blue_computed.summary_text}</p>
              ) : (
                <p className="text-[11px] text-muted-foreground">No Blue summary yet — refresh after range matches exist.</p>
              )}
              {Array.isArray(blueSnap?.blue_computed?.prioritized_remediations) &&
              blueSnap.blue_computed.prioritized_remediations.length > 0 ? (
                <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] text-muted-foreground">
                  {blueSnap.blue_computed.prioritized_remediations.slice(0, 4).map((r) => (
                    <li key={r.id || r.title}>
                      <span className="font-medium text-foreground">{r.title}</span> — {r.why}
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="mt-2 text-[10px] text-sky-200/90">{blueSnapEventLine}</p>
            </div>
          </div>

          <div className="rounded-lg border border-border/80 bg-muted/15 p-3 text-xs text-muted-foreground">
            <p className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-foreground">Connector status</span>
              <Badge variant={connectorStatusBadgeVariant(connectorStatus)}>{connectorStatusUi}</Badge>
              <span>
                id:{' '}
                <code className="rounded bg-muted px-1">
                  {connectorState?.active_connector_id || 'none'}
                </code>
              </span>
              <span>last seen {connectorSeenAgeLabel}</span>
              {connectorState?.route_status ? <span>route {connectorState.route_status}</span> : null}
            </p>
            {scanControl?.last_job_id ? (
              <p className="mt-1">
                <span className="font-medium text-foreground">Last scan</span> — job{' '}
                <code className="rounded bg-muted px-1">{scanControl.last_job_id}</code> · {scanControl.status || 'unknown'}
                {scanControl?.last_device_count != null ? ` · devices ${scanControl.last_device_count}` : ''}
                {scanControl?.fresh_until_utc ? ` · fresh until ${new Date(scanControl.fresh_until_utc).toLocaleString()}` : ''}
              </p>
            ) : null}
          </div>

          <details className="rounded-lg border border-border/80 bg-muted/10 p-2 text-[11px] leading-relaxed text-muted-foreground">
            <summary className="cursor-pointer font-medium text-foreground">
              Lab checklist: access paths (read before testing)
            </summary>
            <ul className="mt-2 list-disc space-y-2 pl-4">
              <li>
                <span className="font-medium text-foreground">
                  Connector online{connectorRequired ? ' (required)' : ' (optional for auto-scan)'}
                </span>{' '}
                — On <strong>your operator PC</strong> (same machine as this browser, on lab Wi‑Fi), run{' '}
                <code className="rounded bg-muted px-1">aerta/lab-connector</code> with{' '}
                <code className="rounded bg-muted px-1">AERTA_CONNECTOR_API_BASE</code> pointing at this API and{' '}
                <code className="rounded bg-muted px-1">AERTA_CONNECTOR_REGISTRATION_KEY</code> matching the server. Target lab
                machines are never modified.
                {connectorRequired
                  ? ' Keep that one process running — discovery and scans are blocked when offline.'
                  : ' Keep it running for connector-driven discovery.'}
              </li>
              <li>
                <span className="font-medium text-foreground">Viewer relay URL</span> — Set{' '}
                <code className="rounded bg-muted px-1">AERTAGO_RDP_GATEWAY_URL</code> /{' '}
                <code className="rounded bg-muted px-1">AERTAGO_VNC_URL</code> (or <code className="rounded bg-muted px-1">AERTA_NOVNC_URL</code>).
                For <strong>Render + Pages</strong>, use a stable public HTTPS relay (named tunnel / VPS). Quick Tunnel{' '}
                (<code className="rounded bg-muted px-1">*.trycloudflare.com</code>) expires when{' '}
                <code className="rounded bg-muted px-1">cloudflared</code> stops — update the runtime URL fields above and save.
              </li>
              <li>
                <span className="font-medium text-foreground">Target PC</span> — Enable RDP or VNC and allow access from
                your approved relay or OOB path on the LAN. Use manual allowlist if auto-scan is paused.
              </li>
              <li>
                <span className="font-medium text-foreground">Exercise run + evidence</span> — Use <strong>Start / continue run</strong> so
                scans and approvals carry the same <code className="rounded bg-muted px-1">run_id</code>; download <strong>Export run bundle</strong> after the session.
              </li>
              <li>
                <span className="font-medium text-foreground">Keyboard and mouse in the viewer</span> — After <strong>Start viewer</strong>, click
                once inside the desktop frame so focus is on the remote session; then keyboard and mouse go to the lab PC (browser and relay
                must allow clipboard/embed as in the gateway notes below).
              </li>
            </ul>
          </details>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2 rounded-md border border-primary/25 bg-primary/[0.05] p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-primary">1) Auto scan (connector-backed)</p>
              <p className="text-[11px] text-muted-foreground">
                Starts <code className="rounded bg-muted px-1">/api/dojo-lab/aertago-control/scan-now</code>, polls job status,
                and syncs discovered targets into allowlist workflow. An <strong>active exercise run</strong> tags the scan with the same{' '}
                <code className="rounded bg-muted px-1">run_id</code> in control state.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  onClick={() => runAutomatedLabSetup()}
                  disabled={controlBusy || labBootstrapBusy || autoScanBusy}
                >
                  {labBootstrapBusy ? 'Automating…' : 'Automate lab setup'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={autoScanAndAllowlist}
                  disabled={controlBusy || autoScanBusy || labBootstrapBusy || connectorStatus !== 'online'}
                >
                  {autoScanBusy ? 'Auto scanning…' : 'Scan only'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => loadAertagoControlConfig({ silent: false })}
                  disabled={controlBusy || autoScanBusy}
                >
                  Refresh control
                </Button>
              </div>
              {connectorStatus !== 'online' ? (
                <p className="text-[11px] text-amber-300">
                  {connectorRequired
                    ? 'Auto scan blocked: start aerta/lab-connector on this operator PC (on lab Wi‑Fi).'
                    : 'Auto scan is paused until connector is online.'}
                </p>
              ) : null}
              {autoScanJobVisual &&
              (autoScanJobVisual.status === 'queued' || autoScanJobVisual.status === 'running') ? (
                <NetworkScanLive
                  status={autoScanJobVisual.status}
                  jobId={autoScanJobVisual.jobId}
                  targetsLabel={autoScanJobVisual.targetsLabel}
                  includeNoPorts={autoScanJobVisual.includeNoPorts}
                  startedAt={autoScanJobVisual.startedAt}
                />
              ) : null}
              {autoScanJobVisual &&
              (autoScanJobVisual.status === 'completed' || autoScanJobVisual.status === 'failed') ? (
                <NetworkScanTerminalBanner
                  status={autoScanJobVisual.status}
                  errorText={autoScanJobVisual.errorText}
                />
              ) : null}
              {autoScanSummary ? <p className="text-[11px] text-muted-foreground">{autoScanSummary}</p> : null}
              {autoScanDiagnostics ? (
                <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-medium uppercase text-muted-foreground">Scan diagnostics</span>
                    <Badge variant="destructive" className="text-[10px]">
                      {autoScanDiagnostics.code}
                    </Badge>
                    {autoScanDiagnostics.ts ? (
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(autoScanDiagnostics.ts).toLocaleString()}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[11px] text-destructive">{autoScanDiagnostics.message}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      onClick={() => autoScanAndAllowlist()}
                      disabled={controlBusy || autoScanBusy || connectorStatus !== 'online'}
                    >
                      Retry auto-scan
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-7 gap-1 text-[11px]"
                      onClick={exportDemoPacket}
                    >
                      <Download className="h-3 w-3" />
                      Export demo packet
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1 text-[11px] text-muted-foreground"
                    onClick={exportDemoPacket}
                  >
                    <Download className="h-3 w-3" />
                    Export demo packet (JSON)
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2 rounded-md border border-dashed border-border/70 bg-muted/10 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Manual fallback allowlist</p>
              <textarea
                className="min-h-[110px] w-full rounded-md border border-border bg-background px-3 py-2 text-xs"
                value={controlTargetInput}
                onChange={(e) => setControlTargetInput(e.target.value)}
                placeholder={'one target per line (ip or hostname)\nexample: 192.168.50.21'}
              />
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={saveAllowlist} disabled={controlBusy}>
                  Save fallback allowlist
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">2) Approve and start session</p>
              <div className="grid gap-2">
                <label className="text-xs text-muted-foreground">
                  Target
                  <select
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-xs"
                    value={controlTarget}
                    onChange={(e) => setControlTarget(e.target.value)}
                  >
                    <option value="">Select allowlisted target</option>
                    {(controlConfig?.allowlist_targets || []).map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-muted-foreground">
                  Protocol
                  <select
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-xs"
                    value={controlProtocol}
                    onChange={(e) => setControlProtocol(e.target.value)}
                  >
                    <option value="rdp">RDP</option>
                    <option value="vnc">VNC</option>
                  </select>
                </label>
                <label className="text-xs text-muted-foreground">
                  Approval reason
                  <input
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-xs"
                    value={controlReason}
                    onChange={(e) => setControlReason(e.target.value)}
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={approveViewerSession} disabled={controlBusy}>
                    Approve target
                  </Button>
                  <Button type="button" size="sm" onClick={startViewerSession} disabled={controlBusy || !approvalId}>
                    Start viewer
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={startAllAllowlistedSessions} disabled={controlBusy}>
                    Start all allowlisted
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={stopViewerSession}
                    disabled={controlBusy || !activeSessionId}
                  >
                    Stop viewer
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-2 rounded-md border border-rose-500/25 bg-rose-500/[0.05] p-3 text-xs text-muted-foreground">
              <p className="text-xs font-medium uppercase tracking-wide text-rose-300">Red AI autonomous run (UI scaffold)</p>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                This panel is a <span className="font-medium text-foreground">front-end placeholder</span> for your executor/agent integration.
                It does not assert outcomes as facts. Wire your backend to set a run status + evidence, then drive viewer start from those results.
              </p>
              {networkScanJob?.tracked?.job?.status ? (
                <>
                  <NetworkScanLive
                    status={networkScanJob.tracked.job.status}
                    jobId={networkScanJob.tracked.jobId}
                    targetsLabel={networkScanJob.tracked.targetsLabel}
                    includeNoPorts={networkScanJob.tracked.includeNoPorts}
                    startedAt={networkScanJob.tracked.scanStartedAt}
                  />
                  <NetworkScanTerminalBanner
                    status={networkScanJob.tracked.job.status}
                    errorText={networkScanJob.tracked.job.error_text}
                  />
                </>
              ) : null}
              <div className="grid gap-2">
                <div className="rounded border border-border/60 bg-background/50 px-2 py-1.5 text-[10px]">
                  <span className="font-medium text-foreground">Selected target</span>
                  <p className="font-mono text-muted-foreground">{controlTarget || '—'}</p>
                </div>
                <div className="rounded border border-border/60 bg-background/50 p-2 text-[10px]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-foreground">Network scan (discovery)</span>
                    <span className="text-muted-foreground">
                      Runs on the <span className="font-medium text-foreground">API host</span>
                    </span>
                  </div>
                  <label className="mt-2 block text-[11px] text-muted-foreground">
                    Targets (CIDR / IP)
                    <input
                      className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
                      value={redAiScanTargets}
                      onChange={(e) => setRedAiScanTargets(e.target.value)}
                      placeholder="e.g. 192.168.1.0/24"
                    />
                  </label>
                  <label className="mt-2 block text-[11px] text-muted-foreground">
                    Ports (optional, comma-separated)
                    <input
                      className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground"
                      value={redAiScanPorts}
                      onChange={(e) => setRedAiScanPorts(e.target.value)}
                      placeholder="leave empty for default probe list"
                    />
                  </label>
                  <label className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={redAiScanIncludeNoPorts}
                      onChange={(e) => setRedAiScanIncludeNoPorts(e.target.checked)}
                    />
                    Include reachable hosts with no open ports
                  </label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={queueRedAiNetworkScan}
                      disabled={redAiScanBusy || !context?.customer_id}
                      title="Queues /api/network-scan/{customer_id}/jobs"
                    >
                      {redAiScanBusy ? 'Queuing…' : 'Queue network scan'}
                    </Button>
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    For lab Wi‑Fi: deploy the API inside that network (or via VPN/tunnel). Optional guardrails: set{' '}
                    <code className="rounded bg-muted px-1">AERTA_SCAN_ALLOWLIST</code> to your authorized CIDRs.
                  </p>
                </div>
                <div className="rounded border border-border/60 bg-background/50 px-2 py-1.5 text-[10px]">
                  <span className="font-medium text-foreground">Run status</span>
                  <p className="font-mono text-muted-foreground">{redAiUiStatus}</p>
                </div>
                <label className="text-[11px] text-foreground">
                  Evidence (JSON, populated by your executor later)
                  <textarea
                    className="mt-1 min-h-[84px] w-full rounded-md border border-border bg-background p-2 font-mono text-[10px] text-foreground"
                    value={redAiUiEvidenceText}
                    onChange={(e) => setRedAiUiEvidenceText(e.target.value)}
                    spellCheck={false}
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled
                    title="Backend integration required"
                  >
                    Start Red AI run
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setRedAiUiStatus('awaiting_backend')}
                    disabled={controlBusy}
                    title="UI-only: mark state while wiring backend"
                  >
                    Mark awaiting backend
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setRedAiUiStatus('reported_success')}
                    disabled={controlBusy}
                    title="UI-only: represent executor-reported success"
                  >
                    Mark reported success
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    onClick={openViewerForRedAiReportedSuccess}
                    disabled={controlBusy || redAiUiStatus !== 'reported_success' || !controlTarget}
                    title="Opens the live viewer session when backend reports success"
                  >
                    Open live viewer
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setRedAiUiStatus('reported_failed')}
                    disabled={controlBusy}
                    title="UI-only: represent executor-reported failure"
                  >
                    Mark reported failed
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Suggested wiring: executor writes <code className="rounded bg-muted px-1">status</code> +{' '}
                <code className="rounded bg-muted px-1">evidence</code> (run id, technique ids, session ids). Then enable
                viewer start when your backend reports a validated access channel.
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-border/80 bg-muted/20 p-3 text-xs text-muted-foreground">
            <p className="mb-1 flex flex-wrap items-center gap-2">
              <span className="font-medium text-foreground">Gateway health</span>
              <Badge variant={gatewayHealthBadgeVariant(gatewayHealth)}>
                {!gatewayHealth
                  ? 'unknown'
                  : gatewayHealth.path_or_route_mismatch
                    ? '404 — fix path/URL'
                    : gatewayHealth.reachable
                      ? gatewayHealth.embed_likely_blocked
                        ? 'reachable (embed warning)'
                        : 'reachable'
                      : 'unreachable'}
              </Badge>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                onClick={() => probeGatewayHealth(controlProtocol, { silent: false })}
                disabled={gatewayBusy}
              >
                {gatewayBusy ? 'Probing…' : 'Probe gateway'}
              </Button>
            </p>
            <p>
              <span className="font-medium text-foreground">Gateway URLs</span> — RDP:{' '}
              <code className="rounded bg-muted px-1">{controlConfig?.gateway_urls?.rdp || 'not configured'}</code> ·
              VNC: <code className="rounded bg-muted px-1">{controlConfig?.gateway_urls?.vnc || 'not configured'}</code>
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Source: <code className="rounded bg-muted px-1">{controlConfig?.gateway_urls_source || 'env'}</code>
            </p>
            {String(controlConfig?.gateway_urls_source || '') === 'autodetected' ? (
              <p className="mt-1 text-[11px] text-emerald-300">
                <span className="font-medium text-foreground">Auto-detected relay</span> — AERTAGO selected a reachable
                viewer relay automatically. RDP:{' '}
                <code className="rounded bg-muted px-1">{controlConfig?.gateway_urls?.rdp || 'n/a'}</code> · VNC:{' '}
                <code className="rounded bg-muted px-1">{controlConfig?.gateway_urls?.vnc || 'n/a'}</code>
              </p>
            ) : null}
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <label className="text-[11px] text-muted-foreground">
                RDP gateway URL
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={gatewayRdpUrl}
                  onChange={(e) => setGatewayRdpUrl(e.target.value)}
                  placeholder="https://viewer-relay.example/"
                />
              </label>
              <label className="text-[11px] text-muted-foreground">
                VNC gateway URL
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={gatewayVncUrl}
                  onChange={(e) => setGatewayVncUrl(e.target.value)}
                  placeholder="https://viewer-relay.example/"
                />
              </label>
              <label className="text-[11px] text-muted-foreground">
                RDP probe URL (optional)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={gatewayRdpProbeUrl}
                  onChange={(e) => setGatewayRdpProbeUrl(e.target.value)}
                  placeholder="Optional API-side probe URL"
                />
              </label>
              <label className="text-[11px] text-muted-foreground">
                VNC probe URL (optional)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={gatewayVncProbeUrl}
                  onChange={(e) => setGatewayVncProbeUrl(e.target.value)}
                  placeholder="Optional API-side probe URL"
                />
              </label>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={saveRuntimeGatewayUrls}
                disabled={gatewayBusy}
              >
                Save gateway URLs (runtime)
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={resetRuntimeGatewayUrlsToEnv}
                disabled={gatewayBusy}
              >
                Reset to env
              </Button>
            </div>
            {gatewayHealth?.viewer_gateway_url &&
            gatewayHealth?.gateway_url &&
            gatewayHealth.viewer_gateway_url !== gatewayHealth.gateway_url ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">Health probe URL</span> (API → gateway){' '}
                <code className="rounded bg-muted px-1">{gatewayHealth.gateway_url}</code> — differs from viewer URL above;
                set <code className="rounded bg-muted px-1">AERTAGO_*_GATEWAY_PROBE_URL</code> when using Docker networking.
              </p>
            ) : null}
            {gatewayHealth?.status_code != null ? (
              <p className="mt-1">
                <span className="font-medium text-foreground">HTTP</span> — {gatewayHealth.status_code}
                {gatewayHealth?.x_frame_options ? (
                  <>
                    {' '}
                    · <span className="font-medium text-foreground">X-Frame-Options</span>:{' '}
                    <code className="rounded bg-muted px-1">{gatewayHealth.x_frame_options}</code>
                  </>
                ) : null}
              </p>
            ) : null}
            {gatewayHealth?.hint ? <p className="mt-1">{gatewayHealth.hint}</p> : null}
            {Array.isArray(gatewayHealth?.suggested_actions) && gatewayHealth.suggested_actions.length > 0 ? (
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px]">
                {gatewayHealth.suggested_actions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            ) : null}
            {gatewayHealth?.reason && !gatewayHealth?.reachable ? (
              <p className="mt-1">
                <span className="font-medium text-foreground">Error</span> — {gatewayHealth.reason}
              </p>
            ) : null}
            <details className="mt-2 rounded border border-border/70 bg-background/70">
              <summary className="cursor-pointer px-2 py-1 text-[11px] font-medium text-muted-foreground">
                Quick Tunnel / DNS errors
              </summary>
              <div className="space-y-2 border-t border-border/60 px-2 py-2 text-[11px] text-muted-foreground">
                <p>
                  If the error mentions <strong>Name or service not known</strong> and the URL contains{' '}
                  <code className="rounded bg-muted px-1">trycloudflare.com</code>, the hostname has expired. Start a new{' '}
                  <code className="rounded bg-muted px-1">cloudflared tunnel</code>, copy the new{' '}
                  <code className="rounded bg-muted px-1">https://</code> URL, paste it in the runtime gateway fields above,
                  click <strong>Save gateway URLs (runtime)</strong>, then probe again.
                </p>
              </div>
            </details>
            <details className="mt-2 rounded border border-border/70 bg-background/70">
              <summary className="cursor-pointer px-2 py-1 text-[11px] font-medium text-muted-foreground">
                Gateway embed header guidance
              </summary>
              <div className="space-y-2 border-t border-border/60 px-2 py-2 text-[11px]">
                <p>
                  If iframe shows blank, allow <code className="rounded bg-muted px-1">https://aerta-pilot.pages.dev</code>{' '}
                  as a frame ancestor and avoid strict <code className="rounded bg-muted px-1">X-Frame-Options: DENY</code>.
                </p>
                <pre className="max-w-full overflow-x-auto rounded bg-muted p-2 font-mono">
{`Content-Security-Policy: frame-ancestors 'self' https://aerta-pilot.pages.dev;
# avoid X-Frame-Options DENY/SAMEORIGIN when embedding cross-origin`}
                </pre>
              </div>
            </details>
            <p className="mt-1">
              <span className="font-medium text-foreground">Latest approval</span> —{' '}
              <code className="rounded bg-muted px-1">{approvalId || 'none'}</code>
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">3) Viewer</p>
            <div ref={viewerSectionRef} />
            {activeSessions.length > 0 ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {activeSessions.map((s) => (
                    <Button
                      key={s.id}
                      type="button"
                      size="sm"
                      variant={currentViewerSession?.id === s.id ? 'default' : 'outline'}
                      onClick={() => setActiveSessionId(s.id)}
                    >
                      {s.target} · {String(s.protocol || '').toUpperCase()}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Session <code className="rounded bg-muted px-1">{currentViewerSession?.id}</code> · target{' '}
                  <code className="rounded bg-muted px-1">{currentViewerSession?.target}</code> · protocol{' '}
                  <code className="rounded bg-muted px-1">{String(currentViewerSession?.protocol || '').toUpperCase()}</code>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ml-2 h-6 px-2 text-[11px]"
                    onClick={() => stopViewerSession(currentViewerSession?.id)}
                    disabled={controlBusy || !currentViewerSession?.id}
                  >
                    Stop this session
                  </Button>
                </p>
                <div
                  className="relative"
                  onMouseDown={() => setViewerFocusArmed(false)}
                  onTouchStart={() => setViewerFocusArmed(false)}
                >
                  {viewerFocusArmed ? (
                    <button
                      type="button"
                      className="absolute inset-0 z-10 flex items-center justify-center rounded-md border border-border/60 bg-black/50 px-4 text-sm text-white backdrop-blur-sm"
                      onClick={() => setViewerFocusArmed(false)}
                      title="Click to focus the remote desktop"
                    >
                      Click to focus viewer (keyboard + mouse)
                    </button>
                  ) : null}
                  <iframe
                    title="AERTAGO live viewer"
                    src={currentViewerSession?.viewer_url || ''}
                    className="h-[520px] w-full rounded-md border border-border bg-black"
                    allow="clipboard-read; clipboard-write"
                  />
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-border px-3 py-8 text-center text-sm text-muted-foreground">
                No active viewer session yet. Save allowlist, approve target, then start viewer.
              </div>
            )}
          </div>
          {Array.isArray(controlConfig?.audit_recent) && controlConfig.audit_recent.length > 0 ? (
            <details className="rounded-md border border-border/70 bg-muted/10">
              <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-muted-foreground">
                Recent control audit ({controlConfig.audit_recent.length})
              </summary>
              <div className="space-y-1 border-t border-border/50 px-3 py-2 text-xs text-muted-foreground">
                {controlConfig.audit_recent.slice().reverse().slice(0, 12).map((a) => (
                  <p key={a.id}>
                    <span className="text-foreground">{a.ts_utc}</span> · {a.action} · {a.actor}
                    {a.target ? ` · ${a.target}` : ''}
                    {a.protocol ? ` · ${String(a.protocol).toUpperCase()}` : ''}
                  </p>
                ))}
              </div>
            </details>
          ) : null}
        </CardContent>
      </Card>

      <AertaxRedAiConsole
        caps={embeddedAertaxCaps}
        labSelectedTarget={controlTarget}
        labAllowlistTargets={controlConfig?.allowlist_targets}
        networkScanTracked={networkScanJob?.tracked ?? null}
      />
      </section>

      {error && !status ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">Could not load AERTAGO</CardTitle>
            <CardDescription className="text-destructive/90">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Viewer accounts cannot open this page — ask for user or admin access. Otherwise confirm the API is reachable.
            </p>
            <Button className="mt-3" variant="outline" size="sm" onClick={refreshMain}>
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {loadingMain && !status ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card/50 px-4 py-6">
          <RefreshCw className="h-5 w-5 animate-spin text-primary" />
          <div>
            <p className="font-medium text-foreground">Loading exercise status…</p>
            <p className="text-sm text-muted-foreground">Reading match files on the server (large histories may take a few seconds).</p>
          </div>
        </div>
      ) : null}

      {status ? (
        <Card className="border-primary/30 bg-card/60">
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
              <Activity className="h-5 w-5 shrink-0 text-primary" />
              {rangeEngineFriendlyTitle(rs?.state)}
              <Badge variant={orchestratorStateBadgeVariant(rs?.state)}>
                {rs?.state === 'running'
                  ? 'Running'
                  : rs?.state === 'missing'
                    ? 'Script missing'
                    : rs?.state === 'active'
                      ? 'Active (recent files)'
                      : rs?.state === 'standby'
                        ? 'Standby (artifacts)'
                        : rs?.state === 'not_running'
                          ? 'Not running'
                          : 'Unknown'}
              </Badge>
              <span className="text-xs font-normal text-muted-foreground">
                Updated {status.checked_at ? new Date(status.checked_at).toLocaleString() : '—'}
              </span>
            </CardTitle>
            <CardDescription>{rangeDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.07] to-transparent p-4">
              <p className="font-medium leading-snug text-foreground">{rangeEngineFriendlySubtitle(rs, art)}</p>
            </div>
            {rs?.state === 'not_running' || rs?.state === 'standby' ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" onClick={startRangeEngine}>
                  Start range engine
                </Button>
                <span className="text-xs text-muted-foreground">Starts the lab process on the server if it is idle.</span>
              </div>
            ) : null}
            {art?.last_activity_utc ? (
              <p className="text-sm text-muted-foreground">
                Latest lab write <span className="font-medium text-foreground">{formatAgeSeconds(art.age_seconds)}</span>
                {art?.newest_file ? (
                  <>
                    {' '}
                    · <span className="text-xs text-muted-foreground">{String(art.newest_file).split('/').pop()}</span>
                  </>
                ) : null}
              </p>
            ) : null}

            <details className="rounded-lg border border-border/70 bg-muted/15">
              <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-muted-foreground">
                Technical details (process &amp; paths)
              </summary>
                <div className="space-y-3 border-t border-border/60 px-3 py-3 text-xs text-muted-foreground">
                {rs?.detail ? <p className="leading-relaxed text-foreground/90">{rs.detail}</p> : null}
                <div className="flex flex-wrap items-center gap-2">
                  <span>Orchestrator script</span>
                  <Badge variant={status.orchestrator_present ? 'default' : 'secondary'}>
                    {status.orchestrator_present ? 'present' : 'missing'}
                  </Badge>
                  {rs?.source ? (
                    <>
                      <span>·</span>
                      <Badge variant="outline">{rs.source}</Badge>
                    </>
                  ) : null}
                </div>
                {showDiagnostics && docker?.docker_cli === false ? <p className="text-amber-200/90">{docker.hint}</p> : null}
                {docker?.inspect_ok === false && docker?.stderr ? (
                  <p>Docker: {String(docker.stderr).slice(0, 280)}</p>
                ) : null}
                {docker?.inspect_ok && docker?.running === false ? (
                  <p>
                    Container <code className="rounded bg-muted px-1">{docker.container}</code> — {docker.status || '—'}
                  </p>
                ) : null}
                {proc?.running && proc?.pids?.length ? (
                  <div>
                    <p>
                      Host PID(s): <span className="font-mono text-foreground">{proc.pids.join(', ')}</span>
                    </p>
                    {proc.cmdline_sample ? (
                      <p className="mt-1 break-all font-mono text-[11px] text-foreground/85">{proc.cmdline_sample}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </details>

            {showDiagnostics && needsDockerSocketHelp ? (
              <details className="rounded-md border border-amber-500/35 bg-amber-500/10">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-amber-100">
                  Fix Docker access for this API container
                </summary>
                <div className="space-y-3 border-t border-amber-500/30 px-3 py-3 text-xs">
                  <p className="text-amber-100/90">
                    The API container cannot inspect Docker right now. Mount the host socket and restart only the API.
                  </p>
                  <div className="space-y-1">
                    <p className="uppercase tracking-wide text-amber-100/80">Compose patch (api service)</p>
                    <div className="flex flex-wrap gap-2">
                      <pre className="max-w-full flex-1 overflow-x-auto rounded bg-background/80 p-2 font-mono">
                        {dockerSocketComposeSnippet}
                      </pre>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => copyRunbook(dockerSocketComposeSnippet)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="uppercase tracking-wide text-amber-100/80">Restart backend after patch</p>
                    <div className="flex flex-wrap gap-2">
                      <pre className="max-w-full flex-1 overflow-x-auto rounded bg-background/80 p-2 font-mono">
                        cd aerta && docker compose up -d --build api
                      </pre>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => copyRunbook('cd aerta && docker compose up -d --build api')}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </div>
              </details>
            ) : null}

            {showDiagnostics ? (
              <p className="border-t border-border pt-4 text-xs text-muted-foreground">
                Orchestrator output streams in <strong className="text-foreground">Orchestrator console</strong> below. Use
                Copy there for support bundles.
              </p>
            ) : null}

            {showDiagnostics ? <details className="rounded-md border border-border/60 bg-muted/20">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-foreground">
                <BookOpen className="mr-2 inline h-4 w-4 text-primary" />
                Pilot stack &amp; troubleshooting
              </summary>
              <div className="space-y-3 border-t border-border/60 px-3 py-3 text-xs">
                <p className="text-muted-foreground">
                  The full pilot stack (including <code className="rounded bg-muted px-1">dojo-orchestrator</code>) starts with
                  one Compose command — no separate service start is required. Use the section below only to restart one
                  service or follow logs. The console above reads Docker logs when available, otherwise the host log file (
                  e.g. on Render).
                </p>
                {hints?.full_stack ? (
                  <div className="flex flex-col gap-1">
                    <span className="uppercase tracking-wide text-muted-foreground">Full stack (pilot / public demo)</span>
                    <div className="flex flex-wrap gap-2">
                      <pre className="max-w-full flex-1 overflow-x-auto rounded bg-background/80 p-2 font-mono">
                        {hints.full_stack}
                      </pre>
                      <Button type="button" variant="outline" size="sm" onClick={() => copyRunbook(hints.full_stack)}>
                        Copy
                      </Button>
                    </div>
                  </div>
                ) : null}
                {hints?.pilot_script_win || hints?.pilot_script_unix ? (
                  <div className="flex flex-col gap-1">
                    <span className="uppercase tracking-wide text-muted-foreground">Helper scripts (pulls Ollama model)</span>
                    <p className="text-muted-foreground">
                      Windows: <code className="rounded bg-muted px-1">{hints?.pilot_script_win || '—'}</code> · Linux/macOS:{' '}
                      <code className="rounded bg-muted px-1">{hints?.pilot_script_unix || '—'}</code>
                    </p>
                  </div>
                ) : null}
                {hints?.restart_orchestrator_only ? (
                  <div className="flex flex-col gap-1">
                    <span className="uppercase tracking-wide text-muted-foreground">Restart AERTAGO orchestrator only</span>
                    <div className="flex flex-wrap gap-2">
                      <pre className="max-w-full flex-1 overflow-x-auto rounded bg-background/80 p-2 font-mono">
                        {hints.restart_orchestrator_only}
                      </pre>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => copyRunbook(hints.restart_orchestrator_only)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                ) : null}
                {hints?.logs_follow ? (
                  <div className="flex flex-col gap-1">
                    <span className="uppercase tracking-wide text-muted-foreground">Follow logs (host terminal)</span>
                    <div className="flex flex-wrap gap-2">
                      <pre className="max-w-full flex-1 overflow-x-auto rounded bg-background/80 p-2 font-mono">
                        {hints.logs_follow}
                      </pre>
                      <Button type="button" variant="outline" size="sm" onClick={() => copyRunbook(hints.logs_follow)}>
                        Copy
                      </Button>
                    </div>
                  </div>
                ) : null}
                {!hints?.full_stack ? (
                  <div className="flex flex-wrap gap-2">
                    <pre className="max-w-full flex-1 overflow-x-auto rounded bg-background/80 p-2 font-mono">
                      cd aerta && docker compose up -d --build
                    </pre>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyRunbook('cd aerta && docker compose up -d --build')}
                    >
                      Copy
                    </Button>
                  </div>
                ) : null}
              </div>
            </details> : null}
          </CardContent>
        </Card>
      ) : null}

      {status ? (
        <div className="animate-fade-in-up relative overflow-hidden rounded-xl border border-primary/25 bg-gradient-to-br from-card via-card/95 to-cyan-500/[0.06] shadow-[0_0_48px_-16px_rgba(59,130,246,0.28)] motion-reduce:animate-none motion-reduce:shadow-none">
          <div
            className="pointer-events-none absolute -left-20 -top-24 h-56 w-56 rounded-full bg-primary/15 blur-3xl animate-aurora-alive motion-reduce:animate-none"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-cyan-500/10 blur-3xl animate-aurora-shift motion-reduce:animate-none"
            aria-hidden
          />
          <div className="relative border-b border-border/60 bg-background/25 px-4 py-3 backdrop-blur-sm sm:px-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/35 bg-primary/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                  <Terminal className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-base font-semibold tracking-tight text-foreground">Orchestrator console</h2>
                  <p className="text-xs text-muted-foreground">Live range-engine output (container or host log)</p>
                </div>
                {logPayload?.via ? (
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {orchestratorViaLabel(logPayload.via)}
                  </Badge>
                ) : null}
                {liveOrchestratorTail ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/35 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300/95 motion-reduce:animate-none">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50 motion-reduce:animate-none" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    Live
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex cursor-pointer items-center gap-2 text-[11px] text-muted-foreground">
                  <input
                    type="checkbox"
                    className="rounded border-border"
                    checked={liveOrchestratorTail}
                    onChange={(e) => setLiveOrchestratorTail(e.target.checked)}
                  />
                  Auto-refresh (8s)
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="gap-2"
                  onClick={() => void loadOrchestratorLogs({})}
                  disabled={logLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${logLoading ? 'animate-spin' : ''}`} />
                  Refresh log
                </Button>
                {logPayload?.ok && logPayload?.text ? (
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => copyRunbook(logPayload.text)}>
                    <Copy className="h-4 w-4" />
                    Copy
                  </Button>
                ) : null}
              </div>
            </div>
            {orchestratorLastLine ? (
              <p className="mt-2 max-w-full truncate font-mono text-[11px] text-primary/90" title={orchestratorLastLine}>
                <span className="text-muted-foreground">Latest · </span>
                {orchestratorLastLine}
              </p>
            ) : null}
            {logPayload?.file_mtime_utc ? (
              <p className="mt-1 text-[10px] text-muted-foreground">
                {logPayload.path ? (
                  <>
                    <span className="font-mono">{String(logPayload.path).split('/').slice(-2).join('/')}</span>
                    {' · '}
                  </>
                ) : null}
                updated {new Date(logPayload.file_mtime_utc).toLocaleString()}
              </p>
            ) : null}
            {logPayload?.note ? <p className="mt-1 text-[10px] leading-snug text-muted-foreground/90">{logPayload.note}</p> : null}
          </div>
          <div className="relative px-4 pb-4 pt-2 sm:px-5">
            {status && logPayload === null ? (
              <div className="flex items-center gap-3 py-10 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-primary motion-reduce:animate-none" />
                <span>Connecting to orchestrator output…</span>
              </div>
            ) : null}
            {logPayload?.ok && logPayload?.text ? (
              <pre className="custom-scrollbar max-h-[min(42vh,420px)] overflow-auto rounded-lg border border-border/80 bg-[#050508]/95 p-3 text-[11px] leading-relaxed text-foreground/95 shadow-inner ring-1 ring-white/[0.04]">
                {logPayload.text}
              </pre>
            ) : null}
            {logPayload && logPayload.ok === false ? (
              <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-sm text-amber-100/90">
                <p>{logPayload.hint || 'No orchestrator output yet.'}</p>
                <details className="mt-2">
                  <summary className="cursor-pointer text-[11px] text-amber-200/85">Technical details</summary>
                  <p className="mt-1 break-all font-mono text-[10px] text-muted-foreground">
                    {[logPayload.reason, logPayload.file_reason, logPayload.stderr, logPayload.error].filter(Boolean).join(' — ')}
                  </p>
                </details>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {status ? (
        <section id="cyber-range-telemetry" className="scroll-mt-24 space-y-4">
          <p className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">Section 4 — Match telemetry</span> · Charts and files here come
            from the <span className="text-foreground">dojo orchestrator / disk</span>. They do not change your allowlist or
            viewer session — configure those in{' '}
            <a href="#cyber-range-lab-workflow" className="text-primary underline-offset-2 hover:underline">
              Live lab + Red AI
            </a>
            .
          </p>
          <div className="aerta-stagger grid gap-4 lg:grid-cols-2">
          {pools ? (
            <Card className="border-primary/20 bg-gradient-to-br from-secondary/30 to-card/80 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Evolution &amp; learning memory
                </CardTitle>
                <CardDescription>
                  Summarized from <code className="rounded bg-muted px-1 text-xs">evolution_memory.json</code> — Red TTP pool,
                  Blue heuristic pool, and cumulative win stats written by the orchestrator.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {pools.error ? <p className="text-sm text-destructive">{pools.error}</p> : null}
                {pools.note ? <p className="text-sm text-amber-200/90">{pools.note}</p> : null}
                {!pools.exists ? (
                  <p className="text-sm text-muted-foreground">
                    No evolution file yet — run matches with the dojo orchestrator to populate learning pools.
                  </p>
                ) : (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="rounded-lg border border-rose-500/25 bg-rose-500/5 p-4">
                        <p className="text-xs font-medium uppercase text-rose-200/80">Red TTP pool</p>
                        <p className="mt-1 text-3xl font-bold text-foreground">{pools.red_ttp_count ?? 0}</p>
                        <p className="text-xs text-muted-foreground">evolved entries</p>
                      </div>
                      <div className="rounded-lg border border-sky-500/25 bg-sky-500/5 p-4">
                        <p className="text-xs font-medium uppercase text-sky-200/80">Blue heuristics</p>
                        <p className="mt-1 text-3xl font-bold text-foreground">{pools.blue_heuristic_count ?? 0}</p>
                        <p className="text-xs text-muted-foreground">evolved entries</p>
                      </div>
                      <div className="rounded-lg border border-border bg-background/50 p-4">
                        <p className="text-xs font-medium uppercase text-muted-foreground">Red (cumulative)</p>
                        <p className="mt-1 text-lg font-semibold text-foreground">
                          {cumR?.win_rate != null ? `${Math.round(Number(cumR.win_rate) * 1000) / 10}% wins` : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {cumR?.total_matches != null ? `${cumR.total_matches} matches` : ''}
                        </p>
                      </div>
                      <div className="rounded-lg border border-border bg-background/50 p-4">
                        <p className="text-xs font-medium uppercase text-muted-foreground">Blue (cumulative)</p>
                        <p className="mt-1 text-lg font-semibold text-foreground">
                          {cumB?.win_rate != null ? `${Math.round(Number(cumB.win_rate) * 1000) / 10}% wins` : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {cumB?.total_matches != null ? `${cumB.total_matches} matches` : ''}
                        </p>
                      </div>
                    </div>
                    {pools.performance_match_keys != null ? (
                      <p className="text-xs text-muted-foreground">
                        Performance score records: {pools.performance_match_keys} matches
                      </p>
                    ) : null}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-rose-200/90">Top Red TTPs (by score)</p>
                        <ul className="space-y-2 text-sm">
                          {(pools.red_top || []).length ? (
                            pools.red_top.map((r, i) => (
                              <li
                                key={`${r.name}-${i}`}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-background/40 px-2 py-1.5"
                              >
                                <span className="text-foreground">{r.name || '—'}</span>
                                <span className="font-mono text-xs text-muted-foreground">
                                  score {r.score ?? '—'} · gen {r.generation ?? '—'}
                                </span>
                              </li>
                            ))
                          ) : (
                            <li className="text-muted-foreground">No ranked entries yet.</li>
                          )}
                        </ul>
                      </div>
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-sky-200/90">
                          Top Blue heuristics (by effectiveness)
                        </p>
                        <ul className="space-y-2 text-sm">
                          {(pools.blue_top || []).length ? (
                            pools.blue_top.map((b, i) => (
                              <li
                                key={`${b.name}-${i}`}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-background/40 px-2 py-1.5"
                              >
                                <span className="text-foreground">{b.name || '—'}</span>
                                <span className="font-mono text-xs text-muted-foreground">
                                  eff. {b.effectiveness_score ?? '—'} · gen {b.generation ?? '—'}
                                </span>
                              </li>
                            ))
                          ) : (
                            <li className="text-muted-foreground">No ranked entries yet.</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Card className="border-border/80 bg-card/80 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="h-5 w-5 text-primary" />
                Current match
              </CardTitle>
              <CardDescription>Active or last-known exercise state from the range.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {cm?.exists === false ? (
                <p className="text-sm text-muted-foreground">No current match file yet — the orchestrator may not have written one.</p>
              ) : null}
              {cm?.error ? <p className="text-sm text-destructive">{cm.error}</p> : null}
              {cm?.note ? <p className="text-sm text-amber-200/90">{cm.note}</p> : null}
              {summary?.present ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-border bg-background/50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Match</p>
                    <p className="mt-1 font-mono text-sm text-foreground">{summary.match_id || '—'}</p>
                    <p className="mt-2 text-xs text-muted-foreground">Started (UTC)</p>
                    <p className="text-sm text-foreground">{summary.start_time_utc || '—'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
                      <p className="text-xs font-medium text-rose-200/90">Red team</p>
                      <p className="mt-1 text-2xl font-semibold text-foreground">
                        {summary.red_cumulative?.win_rate != null
                          ? `${Math.round(Number(summary.red_cumulative.win_rate) * 1000) / 10}%`
                          : '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        W {summary.red_cumulative?.total_wins ?? '—'} · L {summary.red_cumulative?.total_losses ?? '—'}
                      </p>
                    </div>
                    <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3">
                      <p className="text-xs font-medium text-sky-200/90">Blue team</p>
                      <p className="mt-1 text-2xl font-semibold text-foreground">
                        {summary.blue_cumulative?.win_rate != null
                          ? `${Math.round(Number(summary.blue_cumulative.win_rate) * 1000) / 10}%`
                          : '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        W {summary.blue_cumulative?.total_wins ?? '—'} · L {summary.blue_cumulative?.total_losses ?? '—'}
                      </p>
                    </div>
                  </div>
                  {summary.evolved_ttps_preview?.length ? (
                    <div className="sm:col-span-2">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Tactics in play (sample · {summary.evolved_ttps_total ?? 0} tracked)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {summary.evolved_ttps_preview.map((t, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className={
                              t.success === true
                                ? 'border-emerald-500/40 text-emerald-200'
                                : t.success === false
                                  ? 'border-amber-500/40 text-amber-200'
                                  : ''
                            }
                          >
                            {t.name || t.attack_type || 'TTP'}
                            {t.success != null ? (t.success ? ' · ok' : ' · blocked') : ''}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : cm?.exists ? (
                <p className="text-sm text-muted-foreground">No summary could be built for the current match file.</p>
              ) : null}
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ListOrdered className="h-5 w-5 text-primary" />
                Recent results
              </CardTitle>
              <CardDescription>
                Latest finished matches
                {recent?.total_matches != null ? ` · ${recent.total_matches} total in history` : ''}
                {recent?.file_bytes != null ? ` · file ${(recent.file_bytes / 1024).toFixed(0)} KB` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recent?.note ? <p className="mb-3 text-sm text-amber-200/90">{recent.note}</p> : null}
              {recent?.error ? <p className="text-sm text-destructive">{recent.error}</p> : null}
              {!recent?.exists ? (
                <p className="text-sm text-muted-foreground">No match history file yet.</p>
              ) : null}
              {recent?.recent?.length ? (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">When (UTC)</th>
                        <th className="px-3 py-2">Scenario</th>
                        <th className="px-3 py-2">Winner</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.recent.map((row, i) => (
                        <tr key={row.match_id || i} className="border-b border-border/60 last:border-0">
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-muted-foreground">
                            {row.timestamp_utc ? row.timestamp_utc.replace('T', ' ').slice(0, 19) : '—'}
                          </td>
                          <td className="max-w-[220px] truncate px-3 py-2 text-foreground" title={row.scenario}>
                            {row.scenario || '—'}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={winnerBadgeClass(row.winner)}>
                              {row.winner || '—'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : recent?.exists && !recent?.note ? (
                <p className="text-sm text-muted-foreground">No rows parsed in match history.</p>
              ) : null}
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Brain className="h-5 w-5 text-primary" />
                Red vs Blue — performance
              </CardTitle>
              <CardDescription>
                Recent window (last {recent?.recent?.length ?? 0} matches): outcomes, cumulative win %, and evolution
                memory when available.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {recent?.recent?.length ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-center animate-fade-in-up">
                      <p className="text-2xl font-bold tabular-nums text-rose-200">{outcomeCounts.red}</p>
                      <p className="text-[10px] uppercase tracking-wide text-rose-200/80">Red wins</p>
                    </div>
                    <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-center animate-fade-in-up">
                      <p className="text-2xl font-bold tabular-nums text-sky-200">{outcomeCounts.blue}</p>
                      <p className="text-[10px] uppercase tracking-wide text-sky-200/80">Blue wins</p>
                    </div>
                    <div className="rounded-lg border border-zinc-600/40 bg-zinc-800/40 px-4 py-3 text-center animate-fade-in-up">
                      <p className="text-2xl font-bold tabular-nums text-zinc-200">{outcomeCounts.draw}</p>
                      <p className="text-[10px] uppercase tracking-wide text-zinc-400">Draws</p>
                    </div>
                  </div>
                  {outcomeCounts.unknown ? (
                    <p className="text-xs text-amber-200/90">
                      {outcomeCounts.unknown} match(es) had an unrecognized winner label — counted outside Red/Blue.
                    </p>
                  ) : null}
                  <div>
                    <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">Match timeline (oldest → newest)</p>
                    <div className="flex min-h-[28px] flex-wrap items-end gap-0.5 rounded-md border border-border/60 bg-background/40 p-2">
                      {chrono.map((row, i) => {
                        const side = row?.winner_side || normalizeWinnerSide(row?.winner);
                        const fill =
                          side === 'red'
                            ? 'bg-rose-500 shadow-[0_0_8px_rgba(251,113,133,0.35)]'
                            : side === 'blue'
                              ? 'bg-sky-500 shadow-[0_0_8px_rgba(56,189,248,0.35)]'
                              : side === 'draw'
                                ? 'bg-zinc-500'
                                : 'bg-zinc-700';
                        const tip = `${row?.scenario || 'Match'} — ${row?.winner || side}`;
                        return (
                          <div
                            key={row.match_id || `m-${i}`}
                            title={tip}
                            className={`h-7 w-1.5 rounded-sm transition-transform duration-300 hover:scale-y-110 ${fill}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="h-[160px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={outcomeBarData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border opacity-30" vertical={false} />
                        <XAxis dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(24, 24, 27, 0.95)',
                            border: '1px solid #3f3f46',
                            borderRadius: '8px',
                            fontSize: '12px',
                          }}
                          formatter={(v) => [v, 'Matches']}
                        />
                        <Bar dataKey="wins" radius={[6, 6, 0, 0]} animationDuration={600}>
                          {outcomeBarData.map((entry) => (
                            <Cell key={entry.name} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No matches in this window yet — run the range to populate history.</p>
              )}

              {evolutionWinRates.length > 0 ? (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                  <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">Evolution memory (cumulative)</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {evolutionWinRates.map((row) => (
                      <div key={row.name} className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: row.fill }} />
                        <span className="text-muted-foreground">{row.name}:</span>
                        <span className="font-heading font-semibold tabular-nums text-foreground">{row.rate}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div>
                <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">Cumulative win-rate trend</p>
                {trendData.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No rows to chart.</p>
                ) : trendData.length === 1 ? (
                  <p className="text-sm text-muted-foreground">
                    One match in this window — add another finished match to draw a trend line. Outcomes above are still
                    live.
                  </p>
                ) : allWinRatesFlat ? (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-200/90">
                      Every recent match in this window was a draw or had no Red/Blue winner — so cumulative Red/Blue win %
                      stays at 0. Use the bar chart and timeline above for activity.
                    </p>
                    <div className="h-[220px] w-full opacity-90">
                      <ResponsiveContainer width="100%" height="100%" key={`draw-trend-${status?.checked_at}`}>
                        <LineChart data={trendData} margin={{ top: 8, right: 16, bottom: 8, left: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" opacity={0.4} />
                          <XAxis dataKey="match" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                          <YAxis domain={[0, 100]} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                          <ReferenceLine y={0} stroke="#52525b" strokeDasharray="2 2" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(24, 24, 27, 0.95)',
                              border: '1px solid #3f3f46',
                              borderRadius: '8px',
                              fontSize: '12px',
                            }}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="drawRate"
                            name="Draw rate"
                            stroke="#a1a1aa"
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            isAnimationActive
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="h-[280px] w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%" key={`rb-trend-${status?.checked_at}-${trendData.length}`}>
                      <LineChart data={trendData} margin={{ top: 8, right: 16, bottom: 8, left: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" opacity={0.4} />
                        <XAxis dataKey="match" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <ReferenceLine y={0} stroke="#52525b" strokeDasharray="2 2" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(24, 24, 27, 0.95)',
                            border: '1px solid #3f3f46',
                            borderRadius: '8px',
                            fontSize: '12px',
                          }}
                          formatter={(v, name) => [`${v}%`, name]}
                          labelFormatter={(l) => `After match ${l}`}
                        />
                        <Legend />
                        <Line
                          type="linear"
                          dataKey="redWinRate"
                          name="Red AI %"
                          stroke="#fb7185"
                          strokeWidth={3}
                          dot={{ r: 0 }}
                          activeDot={{ r: 6 }}
                          isAnimationActive={false}
                          connectNulls
                        />
                        <Line
                          type="linear"
                          dataKey="blueWinRate"
                          name="Blue AI %"
                          stroke="#38bdf8"
                          strokeWidth={3}
                          dot={{ r: 0 }}
                          activeDot={{ r: 6 }}
                          isAnimationActive={false}
                          connectNulls
                        />
                        <Line
                          type="linear"
                          dataKey="drawRate"
                          name="Draw %"
                          stroke="#a1a1aa"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                          isAnimationActive={false}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Brain className="h-5 w-5 text-primary" />
                Scenarios &amp; learning memory
              </CardTitle>
              <CardDescription>Configured lab scenarios and persisted evolution file (summary).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">
                  Scenarios {scen?.count != null ? `(${scen.count})` : ''}
                </p>
                {scen?.error ? <p className="text-sm text-destructive">{scen.error}</p> : null}
                {!scen?.exists ? (
                  <p className="text-sm text-muted-foreground">No scenarios.yaml found.</p>
                ) : (
                  <ul className="max-h-48 space-y-1 overflow-y-auto text-sm text-foreground">
                    {(scen.names || []).map((name) => (
                      <li key={name} className="flex items-start gap-2 rounded-md bg-muted/30 px-2 py-1">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        {name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border-t border-border pt-4">
                <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">Evolution memory file</p>
                {evo?.error ? <p className="text-sm text-destructive">{evo.error}</p> : null}
                {!evo?.exists ? (
                  <p className="text-sm text-muted-foreground">No evolution memory file on disk yet.</p>
                ) : (
                  <dl className="grid gap-1 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">Updated (UTC)</dt>
                      <dd className="font-mono text-xs">{evo.mtime_utc || '—'}</dd>
                    </div>
                    {evo.red_matches != null ? (
                      <div className="flex justify-between gap-2">
                        <dt className="text-muted-foreground">Red cumulative matches</dt>
                        <dd>{evo.red_matches}</dd>
                      </div>
                    ) : null}
                    {evo.blue_matches != null ? (
                      <div className="flex justify-between gap-2">
                        <dt className="text-muted-foreground">Blue cumulative matches</dt>
                        <dd>{evo.blue_matches}</dd>
                      </div>
                    ) : null}
                  </dl>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card/80">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Learning recommendations
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={recBusyId === 'policy:run'}
                  onClick={runPolicyGate}
                >
                  {recBusyId === 'policy:run' ? 'Running policy gate…' : 'Run policy gate'}
                </Button>
              </div>
              <CardDescription>Transfer suggestions from Red/Blue co-evolution to real defense posture.</CardDescription>
            </CardHeader>
            <CardContent>
              {!learning?.recommendations?.length ? (
                <p className="text-sm text-muted-foreground">No recommendations yet. Run more matches and refresh.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {learning.recommendations.slice(0, 4).map((r) => (
                    <li key={r.id} className="rounded-md border border-border/70 bg-secondary/20 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-medium text-foreground">{r.title}</p>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline">{r.class || 'advisory'}</Badge>
                          <Badge variant="outline">{r.execution_tier || 'low'} tier</Badge>
                          <Badge variant={r.status === 'promoted' ? 'default' : 'secondary'}>{r.status || 'proposed'}</Badge>
                          {r.policy_state ? <Badge variant="secondary">{r.policy_state}</Badge> : null}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{r.why}</p>
                      <p className="mt-1 text-xs">{r.action}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {r.class === 'requires-approval' && r.status === 'proposed' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={recBusyId === `approve:${r.id}`}
                            onClick={() => approveRecommendation(r.id)}
                          >
                            {recBusyId === `approve:${r.id}` ? 'Approving…' : 'Approve'}
                          </Button>
                        ) : null}
                        {r.status === 'approved' ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={recBusyId === `promote:${r.id}`}
                            onClick={() => promoteRecommendation(r.id)}
                          >
                            {recBusyId === `promote:${r.id}` ? 'Promoting…' : 'Promote to pipeline'}
                          </Button>
                        ) : null}
                        {r.status === 'promoted' ? (
                          <Button
                            size="sm"
                            variant="default"
                            disabled={recBusyId === `execute:${r.id}`}
                            onClick={() => executeRecommendation(r.id)}
                          >
                            {recBusyId === `execute:${r.id}` ? 'Executing…' : 'Execute'}
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ListOrdered className="h-5 w-5 text-primary" />
                Policy approval queue
              </CardTitle>
              <CardDescription>
                High-tier learning actions waiting for operator decision.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!policyQueue?.items?.length ? (
                <p className="text-sm text-muted-foreground">No policy queue items yet.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Pending: {policyQueue.pending || 0} / Total: {policyQueue.total || 0}
                  </p>
                  <ul className="space-y-2 text-sm">
                    {policyQueue.items
                      .slice()
                      .reverse()
                      .slice(0, 6)
                      .map((item) => (
                        <li key={item.id} className="rounded-md border border-border/70 bg-secondary/20 px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-medium text-foreground">{item.recommendation_id}</p>
                            <div className="flex items-center gap-1">
                              <Badge variant="outline">{item.execution_tier || 'high'} tier</Badge>
                              <Badge variant={item.status === 'pending_approval' ? 'secondary' : 'outline'}>{item.status}</Badge>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">{item.task_type || 'task'} · {item.id}</p>
                          {item.status === 'pending_approval' ? (
                            <div className="mt-2 flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                disabled={recBusyId === `queue-approve:${item.id}`}
                                onClick={() => approvePolicyQueueItem(item.id)}
                              >
                                {recBusyId === `queue-approve:${item.id}` ? 'Approving…' : 'Approve'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={recBusyId === `queue-reject:${item.id}`}
                                onClick={() => rejectPolicyQueueItem(item.id)}
                              >
                                {recBusyId === `queue-reject:${item.id}` ? 'Rejecting…' : 'Reject'}
                              </Button>
                            </div>
                          ) : null}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        </section>
      ) : null}

      {overview ? (
        <details className="rounded-lg border border-border/60 bg-card/40">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-foreground">Capabilities &amp; server paths</summary>
          <div className="border-t border-border/60 px-4 py-4">
            <p className="mb-3 text-sm text-muted-foreground">{overview.summary}</p>
            <div className="grid gap-2 text-sm">
              <div className="grid gap-1 sm:grid-cols-[140px_1fr] sm:items-baseline">
                <span className="text-muted-foreground">AERTA root</span>
                <code className="break-all rounded bg-muted/80 px-2 py-1 text-xs">{overview.paths?.aerta_root}</code>
              </div>
              <div className="grid gap-1 sm:grid-cols-[140px_1fr] sm:items-baseline">
                <span className="text-muted-foreground">Dojo runtime</span>
                <code className="break-all rounded bg-muted/80 px-2 py-1 text-xs">{overview.paths?.dojo_runtime}</code>
              </div>
            </div>
          </div>
        </details>
      ) : null}

      <Card className="border-dashed border-border bg-card/40">
        <CardHeader>
          <CardTitle className="text-base">Technical export</CardTitle>
          <CardDescription>Full raw JSON for auditors or debugging — can be large and slow.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={loadSnapshot} disabled={loadingSnapshot} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loadingSnapshot ? 'animate-spin' : ''}`} />
            {loadingSnapshot ? 'Loading…' : 'Load full technical JSON'}
          </Button>
          {snapshot ? (
            <>
              <Button type="button" variant="outline" size="sm" className="gap-2" onClick={copySnapshot}>
                <Copy className="h-4 w-4" />
                Copy
              </Button>
              <Button type="button" variant="outline" size="sm" className="gap-2" onClick={downloadSnapshot}>
                <Download className="h-4 w-4" />
                Download
              </Button>
            </>
          ) : null}
        </CardContent>
        {snapshot ? (
          <CardContent className="pt-0">
            <pre className="max-h-[min(40vh,360px)] overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed">
              {snapshotText.slice(0, 120000)}
              {snapshotText.length > 120000 ? '\n… [truncated in browser preview — use Download for full file]' : ''}
            </pre>
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
}

export default CyberRangePage;
